import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import DataApi, { crmAuthHeaders } from '../../api/DataApi';
import TelecmiApi from '../../api/TelecmiApi';
import {
  emitTelecmiOutboundFailed,
  emitTelecmiOutboundWaiting,
} from '../../utils/telecmiDialerBus';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';
import Notification from '../common/Notification';
import AppToast from '../AppToast';
import PathComponent from '../common/PathComponent';
import EmailButton from '../common/EmailButton';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/DetailView.css';
import EditLayoutModal from './EditLayoutModal';
import BulkEditLayout from './BulkEditLayout';
import LeadConvertModal from './LeadConvertModal';
import RelatedTabsModal from './RelatedTabsModal';
import RelatedListView from './RelatedListView';
import WatConnectChat from '../watconnect/chat/WatConnectChat';
import OpportunityProductWizard from './OpportunityProductWizard';
import QuoteProductWizard from './QuoteProductWizard';
import OrderProductWizard from './OrderProductWizard';
import LayoutWidget from './LayoutWidget';
import {
  fetchUiComponentRegistryAllowlist,
  resolveRegisteredTabComponent,
} from './ComponentRegistry';
import { buildDetailCustomActionProps } from '../../utils/detailCustomActionProps';
import OpportunityLineItemApi from '../../api/OpportunityLineItemApi';
import QuoteDocumentApi from '../../api/QuoteDocumentApi';
import GenerateDocumentApi from '../../api/GenerateDocumentApi';
import OpportunityProductApi from '../../api/OpportunityProductApi';
import CurrencyApi from '../../api/CurrencyApi';
//import LayoutList from "./LayoutList";
import jwt_decode from 'jwt-decode';
import { buildTelecmiInitiatePayload } from '../../utils/telecmiInitiatePayload';
import { stashPendingRedirectToast } from '../../utils/pendingRedirectToast';
import { Form, Dropdown, Card, Row, Col, ButtonGroup, Modal } from 'react-bootstrap';
import { FaPlay, FaDownload } from 'react-icons/fa';
import { theme } from '../../theme/theme.js';
import { isFieldVisible } from '../../utils/taskFieldVisibility';
import { isMailConfigFieldVisible } from '../../utils/mailConfigFieldVisibility';
import { formatDate, formatDateTime, formatTime, formatNumber, formatCurrency, formatCalculatedValue, normalizeCalculatedDisplayConfig } from '../../utils/formatUtils';
import {
  buildExchangeRateLookupFromCurrencyList,
  resolveEffectiveExchangeRate,
} from '../../utils/reportCurrencyAggregate';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import TelecmiRecordingPlayerModal from '../common/TelecmiRecordingPlayerModal';
import { downloadTelecmiRecordingFile } from '../common/TelecmiRecordingAudio';
// Import barcode and QR code libraries
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';

import AudienceModal from '../email/AudienceModal';
// Dummy layout data

const ensurePlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return { ...value };
};

const parseDisplayConfig = (displayConfig) => {
  if (!displayConfig) {
    return {};
  }

  if (typeof displayConfig === 'string') {
    try {
      const parsed = JSON.parse(displayConfig);
      return ensurePlainObject(parsed);
    } catch (error) {
      console.warn('Failed to parse display_config JSON:', error);
      return {};
    }
  }

  return ensurePlainObject(displayConfig);
};

const flattenDisplayConfig = (displayConfig) => {
  const root = parseDisplayConfig(displayConfig);
  const calculated = ensurePlainObject(root.calculated);
  const merged = { ...root, ...calculated };
  delete merged.calculated;

  return {
    root,
    calculated,
    merged
  };
};

const DEFAULT_RELATED_LIST_ACTIONS = ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];

const normalizeTabActions = (actions) => {
  if (Array.isArray(actions) && actions.length > 0) {
    const normalized = actions
      .map(action => String(action).toLowerCase())
      .filter(action => DEFAULT_RELATED_LIST_ACTIONS.includes(action));

    return normalized.length > 0 ? Array.from(new Set(normalized)) : [...DEFAULT_RELATED_LIST_ACTIONS];
  }

  return [...DEFAULT_RELATED_LIST_ACTIONS];
};

/** Interpolate {{recordId}}, {{id}}, {{objectName}} for custom detail actions from actions.target_url */
function interpolateDetailCustomActionUrl(template, objectName, recordId) {
  if (!template || typeof template !== 'string') {
    return '';
  }
  const oid = recordId != null ? String(recordId) : '';
  const on = objectName != null ? String(objectName) : '';
  return template
    .replace(/\{\{\s*recordId\s*\}\}/gi, oid)
    .replace(/\{\{\s*id\s*\}\}/gi, oid)
    .replace(/\{\{\s*objectName\s*\}\}/gi, on);
}

/** Custom script return value: navigate to a detail page (same app only; no open redirect). */
function resolveCustomScriptRedirectUrl(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (payload.redirectPath != null && String(payload.redirectPath).trim() !== '') {
    const p = String(payload.redirectPath).trim();
    if (p.startsWith('/view/') && !p.startsWith('//') && !p.includes('://')) {
      return p;
    }
    return null;
  }
  const rv = payload.redirectView;
  if (rv && typeof rv === 'object') {
    const on = rv.objectName != null ? String(rv.objectName).trim() : '';
    const rid = rv.recordId != null ? String(rv.recordId).trim() : '';
    if (on && rid && /^[a-z0-9_]+$/i.test(on)) {
      return `/view/${encodeURIComponent(on)}/${encodeURIComponent(rid)}`;
    }
  }
  if (
    payload.redirectToNewContact === true &&
    payload.data &&
    typeof payload.data === 'object' &&
    payload.data.contact &&
    payload.data.contact.id != null
  ) {
    const cid = String(payload.data.contact.id).trim();
    if (cid) {
      return `/view/contact/${encodeURIComponent(cid)}`;
    }
  }
  return null;
}

/** Allowed `toastVariant` values from custom_script return (Bootstrap Toast `bg`). */
const CUSTOM_SCRIPT_TOAST_VARIANTS = new Set([
  'success',
  'danger',
  'warning',
  'info',
  'primary',
  'secondary',
]);

function normalizeCustomScriptToastVariant(payload) {
  if (!payload || typeof payload !== 'object' || payload.toastVariant == null) {
    return 'success';
  }
  const v = String(payload.toastVariant).toLowerCase().trim();
  return CUSTOM_SCRIPT_TOAST_VARIANTS.has(v) ? v : 'success';
}

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const DetailView = ({ objectName, id, socket, selectedWhatsAppSetting }) => {
  const [activeTab, setActiveTab] = useState('detailsTab');
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const [showTabsDropdown, setShowTabsDropdown] = useState(false);
  const tabsDropdownRef = useRef(null);
  const [recordData, setRecordData] = useState(null);
  const [objectConfig, setObjectConfig] = useState(null);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
  });
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success',
  });
  const [showLayoutManager, setShowLayoutManager] = useState(false);
  /** Lazy-loaded related list rows per tab (key = tab.tabId / related_list_id) */
  const [lazyRelatedTabRecords, setLazyRelatedTabRecords] = useState({});
  const [lazyRelatedTabLoading, setLazyRelatedTabLoading] = useState({});
  const loadedRelatedTabCacheRef = useRef(new Set());
  const inFlightRelatedTabsRef = useRef(new Set());
  const recordDataRef = useRef(recordData);
  recordDataRef.current = recordData;
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);
  const [showLeadConvertModal, setShowLeadConvertModal] = useState(false);
  const [showAddAudience, setShowAddAudience] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showNewRecordModal, setShowNewRecordModal] = useState(false);
  // Show WhatsApp chat by default for Lead and Contact objects (will be updated when record data loads)
  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
  const [updatingPathField, setUpdatingPathField] = useState(false);
  const [bulkEditState, setBulkEditState] = useState({
    show: false,
    tab: null,
    selectedRecords: [],
    parentRecordId: null,
    parentObjectName: null,
  });
  const [showProductWizard, setShowProductWizard] = useState(false);
  const [showQuoteProductWizard, setShowQuoteProductWizard] = useState(false);
  const [showOrderProductWizard, setShowOrderProductWizard] = useState(false);
  const [existingLineItems, setExistingLineItems] = useState([]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [generatingDocument, setGeneratingDocument] = useState(null); // Track which button is generating (by index)
  const [executingCustomScriptId, setExecutingCustomScriptId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteResultMessage, setDeleteResultMessage] = useState(null); // { variant: 'success'|'danger', text: string } | null
  const [telecmiPlayer, setTelecmiPlayer] = useState({ show: false, filename: null });
  const deleteSuccessRedirectRef = useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteChildrenChecked, setDeleteChildrenChecked] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [showRelatedListViewModal, setShowRelatedListViewModal] = useState(false);
  const [currentWidgetTab, setCurrentWidgetTab] = useState(null);
  const tabsContainerRef = useRef(null);
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(10);
  /** Mount sidebar LayoutWidgets after first paint so detail column appears first */
  const [layoutWidgetsMountReady, setLayoutWidgetsMountReady] = useState(false);
  /** From component_registry; null = no allowlist (allow all keys in ComponentRegistry) */
  const [uiTabComponentAllowlist, setUiTabComponentAllowlist] = useState(null);
  const [customDetailActionFrame, setCustomDetailActionFrame] = useState({
    show: false,
    url: '',
    title: '',
  });
  /** Header custom action with action_behavior component */
  const [customDetailActionComponent, setCustomDetailActionComponent] = useState({
    show: false,
    title: '',
    Component: null,
    componentProps: null,
  });

  const getRecordFieldValue = useCallback(
    (fieldName) => {
      if (!recordData || !fieldName) {
        return null;
      }

      /** List/grid sometimes send field-shaped objects { value, label, type, ... } instead of primitives */
      const unwrapScalar = (val) => {
        if (val == null || val === '') return null;
        if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
          const inner = val.value ?? val.displayValue;
          if (inner == null || inner === '') return null;
          if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
            return inner.value ?? inner.displayValue ?? null;
          }
          return inner;
        }
        return val;
      };

      const normalizedName = String(fieldName).toLowerCase();
      const highlightFields = Array.isArray(recordData?.highlightSection)
        ? recordData.highlightSection
        : [];
      const detailFields = (recordData?.details || []).flatMap(section => section.fields || []);
      const hiddenFields = recordData?.otherHiddenFields || [];
      const allFields = [...highlightFields, ...detailFields, ...hiddenFields];

      const match = allFields.find(
        field => field?.fieldName?.toLowerCase() === normalizedName
      );

      const raw = match?.value ?? null;
      if (normalizedName === 'exchange_rate' || normalizedName === 'currency') {
        return unwrapScalar(raw);
      }
      return raw;
    },
    [recordData]
  );

  /** Value to match on the child list (record id for id-based relationships, or e.g. phone for phone-based) */
  const getRelationshipFilterValue = useCallback(
    (sourceFieldName) => {
      if (!sourceFieldName || String(sourceFieldName).toLowerCase() === 'id') {
        return id;
      }
      const raw = getRecordFieldValue(sourceFieldName);
      if (raw == null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const inner = raw.value ?? raw.displayValue;
        if (inner == null || inner === '') {
          return null;
        }
        if (typeof inner === 'object' && inner !== null) {
          return String(inner.value ?? inner.displayValue ?? '');
        }
        return String(inner);
      }
      return String(raw);
    },
    [id, getRecordFieldValue]
  );

  // Get name field value from details section
  const getNameFromDetails = useCallback(() => {
    // Priority order for name fields
    const nameFieldPriorities = [
      'name'
    ];

    // Flatten all fields from all detail sections
    const detailFields = (recordData?.details || []).flatMap(
      section => section.fields || []
    );

    // Also include otherHiddenFields
    const hiddenFields = recordData?.otherHiddenFields || [];
    const allFields = [...detailFields, ...hiddenFields];

    // Search for name fields in priority order
    for (const fieldName of nameFieldPriorities) {
      const field = allFields.find(
        f => f?.fieldName?.toLowerCase() === fieldName.toLowerCase()
      );
      if (field && field.value) {
        return field.value;
      }
    }

    // Fallback: try to get from header titleField if available
    if (recordData?.header?.titleField) {
      return getRecordFieldValue(recordData.header.titleField);
    }

    return null;
  }, [recordData, getRecordFieldValue]);

  // Get currency from record only (no fallback) - used for opportunity/quote product selection
  const getRecordCurrencyOnly = useCallback(() => {
    return getRecordFieldValue('currency') || null;
  }, [getRecordFieldValue]);

  // Centralized token decoding - decode once and reuse
  const decodedToken = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        return jwt_decode(token);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return null;
  }, []);

  // Resolve currency with priority: record currency > token currency (user currency or company currency) > default
  const resolveCurrency = useCallback((fallbackCurrency = 'INR') => {
    // Priority 1: Check currency field on current object (record)
    const recordCurrency = getRecordFieldValue('currency');
    if (recordCurrency) {
      return recordCurrency;
    }

    // Priority 2: Check currency from JWT token
    // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
    // So it will be user.currency if available, otherwise company.currency
    if (decodedToken?.currency) {
      return decodedToken.currency;
    }

    // Fallback to default
    return fallbackCurrency;
  }, [getRecordFieldValue, decodedToken]);

  // Get enable_watconnect from JWT token
  const enableWatConnect = useMemo(() => {
    return decodedToken?.enable_watconnect === true;
  }, [decodedToken]);

  const enableTelecmi = useMemo(() => {
    return decodedToken?.enable_telecmi === true;
  }, [decodedToken]);
  const enableCallyzer = useMemo(() => {
    return decodedToken?.enable_callyzer === true;
  }, [decodedToken]);
  const enableVgrowDialer = useMemo(() => {
    return decodedToken?.enable_vgrow_dialer === true;
  }, [decodedToken]);
  const enableCti = useMemo(() => {
    return enableTelecmi || enableCallyzer || enableVgrowDialer;
  }, [enableTelecmi, enableCallyzer, enableVgrowDialer]);

  // Base currency + current org exchange rates (fallback when record exchange_rate is missing)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const baseRow = list.find((r) => r.is_base_currency === true);
        setBaseCurrencyCode(baseRow?.currency_code ? String(baseRow.currency_code).trim() : null);
        setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(list));
      } catch (e) {
        if (!cancelled) {
          setBaseCurrencyCode(null);
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
      } catch {
        if (!cancelled) setMultiCurrencyEnabled(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    (async () => {
      const allowlist = await fetchUiComponentRegistryAllowlist(ac.signal, objectName);
      if (!cancelled) setUiTabComponentAllowlist(allowlist);
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [objectName]);

  const displayBaseCurrencyCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const displayExchangeRateLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  const relatedTabsSource = useMemo(() => recordData?.tabs || [], [recordData?.tabs]);

  const normalizedRelatedTabs = useMemo(() => {
    if (!Array.isArray(relatedTabsSource)) {
      return [];
    }

    return relatedTabsSource.map(tab => {
      const config = tab?.config || {};
      const normalizedActions = normalizeTabActions(config.actions || tab?.actions);
      const tabId = tab?.tabId != null && tab.tabId !== ''
        ? tab.tabId
        : config.related_list_id;

      return {
        ...tab,
        tabId,
        actions: normalizedActions,
        config: {
          ...config,
          actions: normalizedActions,
        },
      };
    });
  }, [relatedTabsSource]);

  // For email_template, do not show Email (email_log) related list tab
  const displayRelatedTabs = useMemo(() => {
    if (objectName?.toLowerCase() === 'email_template') {
      return normalizedRelatedTabs.filter(t => t.config?.target_object_name?.toLowerCase() !== 'email_log');
    }
    return normalizedRelatedTabs;
  }, [objectName, normalizedRelatedTabs]);

  const displayRelatedTabsWithLazyRecords = useMemo(() => {
    return displayRelatedTabs.map((tab) => {
      const tid = tab.tabId != null ? String(tab.tabId) : null;
      if (tid != null && Object.prototype.hasOwnProperty.call(lazyRelatedTabRecords, tid)) {
        return { ...tab, records: lazyRelatedTabRecords[tid] };
      }
      return { ...tab, records: tab.records || [] };
    });
  }, [displayRelatedTabs, lazyRelatedTabRecords]);

  /** Related-list tabs + search-view layout widgets (same display_order as Related List Manager) */
  const detailTabsMerged = useMemo(() => {
    const searchWidgets = (recordData?.widgets || []).filter((w) => {
      if (String(w.widgetType || '').toLowerCase() !== 'search') return false;
      const cfg = w.config || {};
      return cfg.show_in_detail_tabs !== false;
    });

    const searchTabs = searchWidgets.map(w => {
      const cfg = w.config || {};
      const displayLabel = w.widgetLabel || cfg.search_view_name || 'Search';
      return {
        tabName: `__searchWidget_${w.widgetId}`,
        displayLabel,
        tabId: w.widgetId,
        records: [],
        config: {
          tab_kind: 'search_widget',
          display_order: w.displayOrder ?? 0,
          search_widget: {
            widgetId: w.widgetId,
            widgetType: w.widgetType,
            widgetLabel: w.widgetLabel,
            widgetColumnPosition: w.widgetColumnPosition,
            componentId: w.componentId,
            config: cfg,
            displayOrder: w.displayOrder,
            data: w.data
          }
        },
        _sortKey: w.displayOrder ?? 0
      };
    });

    const relatedPrepared = displayRelatedTabsWithLazyRecords.map(t => ({
      ...t,
      displayLabel: t.tabName,
      _sortKey: t.config?.display_order ?? 0
    }));

    return [...relatedPrepared, ...searchTabs]
      .sort((a, b) => a._sortKey - b._sortKey)
      .map(({ _sortKey, ...tab }) => tab);
  }, [displayRelatedTabsWithLazyRecords, recordData?.widgets]);

  const customTabs = useMemo(() => {
    const rawConfig = objectConfig?.config;
    if (!rawConfig) {
      return [];
    }

    let parsedConfig = rawConfig;
    if (typeof rawConfig === 'string') {
      try {
        parsedConfig = JSON.parse(rawConfig);
      } catch (error) {
        console.warn('Failed to parse object config JSON:', error);
        return [];
      }
    }

    if (!parsedConfig?.customTabs || !Array.isArray(parsedConfig?.tabs)) {
      return [];
    }

    return parsedConfig.tabs;
  }, [objectConfig]);

  const normalizedCustomTabs = useMemo(() => {
    return customTabs.map((tab, index) => {
      const label = tab?.label || tab?.component || `Tab ${index + 1}`;
      const tabName = `custom:${tab?.name || tab?.component || label || index}`;
      return {
        ...tab,
        tabName,
        label
      };
    });
  }, [customTabs]);

  /** Header breadcrumb: use object_config label (human-readable), not API name from view payload */
  const detailPageObjectLabel = useMemo(() => {
    const fromConfig = objectConfig?.object_label && String(objectConfig.object_label).trim();
    if (fromConfig) return fromConfig;
    const fromHeader = recordData?.header?.objectLabel && String(recordData.header.objectLabel).trim();
    if (fromHeader) return fromHeader;
    if (objectName) return objectName;
    return '—';
  }, [objectConfig?.object_label, recordData?.header?.objectLabel, objectName]);

  // Get current user from JWT token
  const getCurrentUser = useCallback(() => {
    if (decodedToken) {
      const user = {
        id: decodedToken.id,
        firstname: decodedToken.firstname,
        lastname: decodedToken.lastname,
        email: decodedToken.email,
        username: decodedToken.username,
      };
      console.log('Current user:', user);
      return user;
    }
    const defaultUser = {
      id: 'current-user-id',
      firstname: 'Current',
      lastname: 'User',
      email: 'current.user@company.com',
    };
    console.log('Using default user:', defaultUser);
    return defaultUser;
  }, [decodedToken]);

  const getBestPhoneNumber = useCallback(() => {
    const candidates = ['phone', 'mobile', 'mobile_no', 'mobile_number', 'contact_no', 'phone_number'];
    for (const fieldName of candidates) {
      const value = getRecordFieldValue(fieldName);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }, [getRecordFieldValue]);

  const handleTelecmiCall = useCallback(async () => {
    if (!enableCti) {
      setToast({
        show: true,
        message: 'CTI solution is not enabled for your organization.',
        variant: 'danger',
      });
      return;
    }
    const phone = getBestPhoneNumber();
    if (!phone) {
      setToast({
        show: true,
        message: 'No phone number available on this record.',
        variant: 'danger',
      });
      return;
    }

    const toNum = Number(String(phone).replace(/\D/g, ''));
    const built = buildTelecmiInitiatePayload(toNum, { crm: true });
    if (built.error) {
      setToast({
        show: true,
        message: built.error,
        variant: 'danger',
      });
      return;
    }
    emitTelecmiOutboundWaiting({ to: toNum });
    try {
      const response = await TelecmiApi.initiateCall(built.payload);
      const message =
        response?.data?.data?.msg ||
        response?.data?.msg ||
        response?.data?.message ||
        response?.data?.data?.message ||
        'Call initiated successfully.';
      setToast({
        show: true,
        message,
        variant: 'success',
      });
    } catch (error) {
      const message =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate call.';
      emitTelecmiOutboundFailed({ message, to: toNum });
      setToast({
        show: true,
        message,
        variant: 'danger',
      });
    }
  }, [getBestPhoneNumber, enableCti]);

  const handleTelecmiCallForNumber = useCallback(async (phoneValue) => {
    if (!enableCti) {
      setToast({
        show: true,
        message: 'CTI solution is not enabled for your organization.',
        variant: 'danger',
      });
      return;
    }
    const normalized = String(phoneValue || '').replace(/\D/g, '');
    if (!normalized) {
      setToast({
        show: true,
        message: 'Invalid phone number.',
        variant: 'danger',
      });
      return;
    }

    const toNum = Number(normalized);
    const built = buildTelecmiInitiatePayload(toNum, { crm: true });
    if (built.error) {
      setToast({
        show: true,
        message: built.error,
        variant: 'danger',
      });
      return;
    }
    emitTelecmiOutboundWaiting({ to: toNum });
    try {
      const response = await TelecmiApi.initiateCall(built.payload);
      const message =
        response?.data?.data?.msg ||
        response?.data?.msg ||
        response?.data?.message ||
        response?.data?.data?.message ||
        'Call initiated successfully.';
      setToast({
        show: true,
        message,
        variant: 'success',
      });
    } catch (error) {
      const message =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate call.';
      emitTelecmiOutboundFailed({ message, to: toNum });
      setToast({
        show: true,
        message,
        variant: 'danger',
      });
    }
  }, [enableCti]);

  // Close dropdown when clicking outside and update position on scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on the portal dropdown
      const portalDropdown = document.querySelector('.tabs-dropdown-menu');
      if (tabsDropdownRef.current && !tabsDropdownRef.current.contains(event.target) &&
        (!portalDropdown || !portalDropdown.contains(event.target))) {
        setShowTabsDropdown(false);
      }
    };

    const handleScroll = () => {
      // Close dropdown on scroll to avoid positioning issues
      if (showTabsDropdown) {
        setShowTabsDropdown(false);
      }
    };

    if (showTabsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showTabsDropdown]);

  const handleBulkEditRequest = ({
    tab,
    selectedRecords = [],
    parentRecordId,
    parentObjectName,
  }) => {
    if (!tab) {
      console.warn('Bulk edit request received without tab data.');
      return;
    }

    setBulkEditState({
      show: true,
      tab,
      selectedRecords,
      parentRecordId,
      parentObjectName,
    });
  };

  // Handle "Show All" from widget
  const handleWidgetShowAll = (tabObject, currentRecordId, sourceObjectName) => {
    console.log('🔍 Opening related list view modal from widget:', tabObject);
    console.log('🔍 Current record ID:', currentRecordId);
    console.log('🔍 Source object name:', sourceObjectName);

    // Validate that we have a valid object name
    const objectName = tabObject.config?.target_object_name;
    if (!objectName) {
      console.error('❌ Missing target_object_name in tab config:', tabObject);
      setToast({
        show: true,
        message: 'Error: No target object name found for this related list.',
        variant: 'danger',
      });
      return;
    }

    // Validate that we have target_field_name for filtering related records
    const targetFieldName = tabObject.config?.target_field_name;
    if (!targetFieldName) {
      console.error('❌ Missing target_field_name in tab config. Cannot filter related records.');
      setToast({
        show: true,
        message: 'Error: Cannot determine relationship field. Related records cannot be filtered.',
        variant: 'danger',
      });
      return;
    }

    console.log('✅ Relationship info:', {
      target_object_name: objectName,
      target_field_name: targetFieldName,
      parentId: currentRecordId,
      sourceObjectName: sourceObjectName
    });

    // Store the current tab in window object for RelatedListView to access
    // This is required for RelatedListView to get field config and other settings
    window.currentModalTab = {
      ...tabObject,
      config: {
        ...tabObject.config,
        target_field_name: targetFieldName, // Ensure target_field_name is set for filtering
        // Preserve field_config if it exists (contains fields to display)
        field_config: tabObject.config?.field_config || undefined
      }
    };

    setCurrentWidgetTab(tabObject);
    setShowRelatedListViewModal(true);
  };

  const fetchExistingLineItems = useCallback(async () => {
    if (!id) {
      console.warn('Cannot load opportunity products: missing opportunity id');
      return;
    }
    setLoadingLineItems(true);
    try {
      console.log('Fetching existing opportunity products for', id);
      const response = await OpportunityLineItemApi.fetchByOpportunity(id);
      console.log('Existing line items response:', response);
      if (response?.success) {
        setExistingLineItems(response.data || []);
      } else {
        setExistingLineItems([]);
      }
    } catch (error) {
      console.error('Failed to fetch opportunity products:', error);
      setExistingLineItems([]);
    } finally {
      setLoadingLineItems(false);
    }
  }, [id]);

  const handleAddProducts = async () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this opportunity before adding products.',
        variant: 'danger',
      });
      return;
    }
    await fetchExistingLineItems();
    setShowProductWizard(true);
  };
  const handleAddQuoteProducts = () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this quote before adding products.',
        variant: 'danger',
      });
      return;
    }
    setShowQuoteProductWizard(true);
  };

  const handleAddOrderProducts = () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this order before adding products.',
        variant: 'danger',
      });
      return;
    }
    setShowOrderProductWizard(true);
  };

  const handleProductWizardClose = () => {
    setShowProductWizard(false);
  };
  const handleQuoteProductWizardClose = () => {
    setShowQuoteProductWizard(false);
  };

  const handleOrderProductWizardClose = () => {
    setShowOrderProductWizard(false);
  };

  const handleProductWizardSaveSuccess = () => {
    setNotification({
      show: true,
      message: 'Products added successfully!',
      type: 'success',
    });
    refreshRecordData();
  };
  const handleQuoteProductWizardSaveSuccess = () => {
    setNotification({
      show: true,
      message: 'Quote products saved successfully!',
      type: 'success',
    });
    refreshRecordData();
  };

  const handleOrderProductWizardSaveSuccess = () => {
    setNotification({
      show: true,
      message: 'Order products saved successfully!',
      type: 'success',
    });
    refreshRecordData();
  };

  const handleSyncProducts = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Quote ID is missing.',
        variant: 'danger',
      });
      return;
    }

    // Get opportunity ID from quote record
    const opportunityId = getRecordFieldValue('opportunity');
    if (!opportunityId) {
      setToast({
        show: true,
        message: 'This quote is not linked to an opportunity. Please link an opportunity first.',
        variant: 'warning',
      });
      return;
    }

    setSyncingProducts(true);
    try {
      const response = await OpportunityProductApi.syncProductsFromQuote({
        quoteId: id,
        opportunityId: opportunityId,
      });

      if (response?.success) {
        setToast({
          show: true,
          message: response.message || `Successfully synced: ${response.added || 0} added, ${response.updated || 0} updated, ${response.removed || 0} removed`,
          variant: 'success',
        });
        refreshRecordData();
      } else {
        setToast({
          show: true,
          message: response?.message || 'Failed to sync products',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      setToast({
        show: true,
        message: error?.message || 'Failed to sync products',
        variant: 'danger',
      });
    } finally {
      setSyncingProducts(false);
    }
  };

  const handleDownloadQuoteDocument = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Quote ID is missing.',
        variant: 'danger',
      });
      return;
    }

    try {
      setToast({
        show: true,
        message: 'Generating quote document...',
        variant: 'info',
      });

      await QuoteDocumentApi.downloadQuoteDocument(id, 'customer quote', 'html');

      setToast({
        show: true,
        message: 'Quote document downloaded successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error downloading quote document:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to download quote document. Please ensure a document template is configured.',
        variant: 'danger',
      });
    }
  };

  const handleGenerateDocument = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Record ID is missing.',
        variant: 'danger',
      });
      return;
    }

    try {
      setToast({
        show: true,
        message: 'Generating document...',
        variant: 'info',
      });

      await GenerateDocumentApi.generateDocument(objectName, id, 'customer quote');

      setToast({
        show: true,
        message: 'Document generated and downloaded successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error generating document:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to generate quote document. Please ensure a document template is configured.',
        variant: 'danger',
      });
    }
  };

  // Handle delete record
  const handleDeleteRecord = () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Record ID is missing.',
        variant: 'danger',
      });
      return;
    }
    setDeleteResultMessage(null);
    setShowDeleteModal(true);
  };

  const confirmDeleteRecord = async () => {
    if (!id) return;

    setIsDeleting(true);

    try {
      const dataApi = new DataApi('object');
      const query = deleteChildrenChecked ? '?cascadeDeleteChildren=true' : '';
      const response = await dataApi.delete(`/${objectName}/${id}${query}`);

      if (response?.data?.success) {
        deleteSuccessRedirectRef.current = true;
        setDeleteResultMessage({ variant: 'success', text: 'Record deleted successfully!' });
      } else {
        const msg = response?.data?.message || 'Failed to delete record.';
        setDeleteResultMessage({ variant: 'danger', text: msg });
      }
    } catch (error) {
      const data = error?.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        error?.message ||
        'Error deleting record. Please try again.';
      setDeleteResultMessage({ variant: 'danger', text: msg });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderReadOnlyCheckbox = rawValue => {
    const checked =
      rawValue === true ||
      rawValue === 'true' ||
      rawValue === 1 ||
      rawValue === '1';

    return (
      <div className='detail-checkbox-wrapper' title={checked ? 'Yes' : 'No'}>
        <Form.Check
          type='checkbox'
          checked={checked}
          readOnly
          disabled
          className='detail-readonly-checkbox'
        />
      </div>
    );
  };

  // Helper function to safely extract primitive value from potentially nested objects
  const extractPrimitiveValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    // If it's already a primitive (not an object or array), return it
    if (typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    // If it's an object, try to extract a primitive value
    // Priority: value > displayValue > convert to string
    if (value.value !== undefined) {
      // Recursively extract if value is also an object
      const extracted = extractPrimitiveValue(value.value);
      if (extracted !== null) return extracted;
    }

    if (value.displayValue !== undefined) {
      const extracted = extractPrimitiveValue(value.displayValue);
      if (extracted !== null) return extracted;
    }

    // If we can't extract a primitive, convert to string as last resort
    try {
      return String(value);
    } catch {
      return null;
    }
  };

  const renderCalculatedField = (field) => {
    const displayConfig = field.displayConfig ?? field.display_config;
    const normalized = normalizeCalculatedDisplayConfig(displayConfig);
    const { merged: mergedConfig } = flattenDisplayConfig(displayConfig);

    // Extract value if field.value is an object (handle nested field objects)
    let value = extractPrimitiveValue(field.value);

    if (value === null || value === undefined || value === '') {
      return '—';
    }

    const resolveNumber = (key, fallback) => {
      const numeric = toFiniteNumber(mergedConfig[key]);
      return numeric !== null ? numeric : fallback;
    };

    const resolveBoolean = (key, fallback) => {
      if (mergedConfig[key] === true || mergedConfig[key] === 'true' || mergedConfig[key] === 1 || mergedConfig[key] === '1') {
        return true;
      }
      if (mergedConfig[key] === false || mergedConfig[key] === 'false' || mergedConfig[key] === 0 || mergedConfig[key] === '0') {
        return false;
      }
      return fallback;
    };

    switch (normalized.returnType) {
      case 'number':
      case 'currency':
      case 'percent':
      case 'boolean':
      case 'text':
        return formatCalculatedValue(value, displayConfig);
      case 'checkbox':
        return renderReadOnlyCheckbox(value);

      case 'date':
        return formatDate(value);

      case 'datetime':
        return formatDateTime(value);

      case 'time':
        return formatTime(value);

      case 'url': {
        const displayText = formatCalculatedValue(value, displayConfig) || String(value);
        return (
          <a
            href={String(value)}
            rel='noopener noreferrer'
            className='link-brand'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontWeight: theme.typography.fontWeight.medium,
              textDecoration: 'none',
              transition: theme.transitions.normal,
              fontSize: theme.typography.fontSize.lg,
            }}
          >
            {displayText}
          </a>
        );
      }

      case 'image': {
        const isBrowser = typeof window !== 'undefined';
        const isMobile = isBrowser ? window.innerWidth <= 768 : false;
        const resolveDimension = (primaryKey, secondaryKey, fallback) => {
          const primary = toFiniteNumber(mergedConfig[primaryKey]);
          if (primary !== null) {
            return primary;
          }
          if (secondaryKey) {
            const secondary = toFiniteNumber(mergedConfig[secondaryKey]);
            if (secondary !== null) {
              return secondary;
            }
          }
          return fallback;
        };

        const width = isMobile
          ? resolveDimension('mobileWidth', 'width', 150)
          : resolveDimension('width', null, 300);
        const height = isMobile
          ? resolveDimension('mobileHeight', 'height', 150)
          : resolveDimension('height', null, 300);

        const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
        const fitRaw = typeof mergedConfig.objectFit === 'string' ? mergedConfig.objectFit.toLowerCase() : '';
        const objectFit = allowedFits.includes(fitRaw) ? fitRaw : 'contain';

        return (
          <img
            src={value}
            alt={field.label}
            style={{
              width: width !== null ? `${width}px` : 'auto',
              height: height !== null ? `${height}px` : 'auto',
              maxWidth: '100%',
              objectFit,
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<span style="color: #999;">Image not available</span>';
            }}
          />
        );
      }

      case 'barcode': {
        const format = typeof mergedConfig.format === 'string' && mergedConfig.format.trim() !== ''
          ? mergedConfig.format.trim().toUpperCase()
          : 'CODE128';
        const width = resolveNumber('width', 2);
        const height = resolveNumber('height', 100);
        const displayValue = resolveBoolean('displayValue', true);

        return (
          <Barcode
            value={String(value)}
            format={format}
            width={width ?? 2}
            height={height ?? 100}
            displayValue={displayValue}
          />
        );
      }

      case 'qrcode': {
        const size = resolveNumber('size', 200);
        const levelRaw = typeof mergedConfig.errorCorrectionLevel === 'string'
          ? mergedConfig.errorCorrectionLevel.toUpperCase()
          : '';
        const validLevels = ['L', 'M', 'Q', 'H'];
        const level = validLevels.includes(levelRaw) ? levelRaw : 'M';

        return (
          <QRCode
            value={String(value)}
            size={size ?? 200}
            level={level}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        );
      }

      default:
        return formatCalculatedValue(value, displayConfig);
    }
  };

  /** Lazy-load one related list tab; results are cached per (objectName, id, tabId). Pass `{ force: true }` to refetch. */
  const fetchSingleRelatedTab = useCallback(
    async (tabId, options = {}) => {
      const force = options.force === true;
      if (!objectName || !id || tabId == null || tabId === '') {
        return;
      }
      const tid = String(tabId);
      const cacheKey = `${objectName}::${id}::${tid}`;
      if (force) {
        loadedRelatedTabCacheRef.current.delete(cacheKey);
      }
      if (!force && loadedRelatedTabCacheRef.current.has(cacheKey)) {
        return;
      }
      if (inFlightRelatedTabsRef.current.has(tid)) {
        return;
      }
      inFlightRelatedTabsRef.current.add(tid);
      setLazyRelatedTabLoading((prev) => ({ ...prev, [tid]: true }));
      try {
        const dataApi = new DataApi(`relatedlist/records/${objectName}/${id}/tab`);
        const response = await dataApi.fetchById(tid);
        const payload = response?.data?.data;
        const records = Array.isArray(payload?.records) ? payload.records : [];
        setLazyRelatedTabRecords((prev) => ({ ...prev, [tid]: records }));
        loadedRelatedTabCacheRef.current.add(cacheKey);
      } catch (e) {
        console.error('Failed to load related tab records:', e);
        setLazyRelatedTabRecords((prev) => ({ ...prev, [tid]: [] }));
        loadedRelatedTabCacheRef.current.add(cacheKey);
      } finally {
        inFlightRelatedTabsRef.current.delete(tid);
        setLazyRelatedTabLoading((prev) => ({ ...prev, [tid]: false }));
      }
    },
    [objectName, id]
  );

  const resolveRelatedListTabFetchId = useCallback((tab) => {
    if (!tab) return null;
    const raw = tab.tabId ?? tab.config?.related_list_id;
    if (raw == null || raw === '') return null;
    return String(raw);
  }, []);

  const switchTab = useCallback(
    (tabName) => {
      setActiveTab(tabName);
      const tabsMeta = recordDataRef.current?.tabs;
      if (!Array.isArray(tabsMeta)) {
        return;
      }
      const meta = tabsMeta.find((t) => t.tabName === tabName);
      const tid = resolveRelatedListTabFetchId(meta);
      if (!tid) {
        return;
      }
      fetchSingleRelatedTab(tid);
    },
    [fetchSingleRelatedTab, resolveRelatedListTabFetchId]
  );

  /** Related list toolbar refresh: one tab API only; does not reload the detail record. */
  const handleRelatedListTabRefresh = useCallback(
    (tab) => {
      const tid = resolveRelatedListTabFetchId(tab);
      if (!tid) return;
      fetchSingleRelatedTab(tid, { force: true });
    },
    [fetchSingleRelatedTab, resolveRelatedListTabFetchId]
  );

  /** After add/update/delete in embedded related grid: refetch active tab rows only (fast). */
  const refreshActiveRelatedTabRowsOnly = useCallback(() => {
    const at = activeTabRef.current;
    if (typeof at === 'string' && at.startsWith('__searchWidget_')) {
      return;
    }
    const tabsMeta = recordDataRef.current?.tabs;
    if (!Array.isArray(tabsMeta)) return;
    const meta = tabsMeta.find((t) => t.tabName === at);
    const tid = meta?.tabId ?? meta?.config?.related_list_id;
    if (tid != null && tid !== '') {
      fetchSingleRelatedTab(String(tid), { force: true });
    }
  }, [fetchSingleRelatedTab]);

  // Function to refresh the record data (including related lists)
  const refreshRecordData = async () => {
    console.log('$$ refreshRecordData');
    try {
      const dataApi = new DataApi(`object/view/${objectName}`);
      const response = await dataApi.fetchById(id);
      const data = response?.data?.data;

      if (!data || Object.keys(data).length === 0) {
        setError('No Record Found!');
      } else {
        loadedRelatedTabCacheRef.current.clear();
        inFlightRelatedTabsRef.current.clear();
        setLazyRelatedTabRecords({});
        setLazyRelatedTabLoading({});
        setRecordData(data);
        console.log('Record data refreshed:', data);
        console.log('Related tabs data refreshed:', data?.tabs);
        const at = activeTabRef.current;
        const meta = Array.isArray(data.tabs) ? data.tabs.find((t) => t.tabName === at) : null;
        const tid = meta?.tabId ?? meta?.config?.related_list_id;
        if (tid != null && tid !== '') {
          fetchSingleRelatedTab(String(tid));
        }
      }
    } catch (error) {
      console.error('Failed to refresh record:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'An unexpected error occurred.';
      setError(message);
    }
  };

  // Handler for path stage changes
  const handlePathStageChange = async (fieldName, newValue) => {
    // Prevent multiple simultaneous updates
    if (updatingPathField) {
      console.log('Update already in progress, ignoring...');
      return;
    }

    try {
      setUpdatingPathField(true);
      console.log(`🔄 Updating ${fieldName} to ${newValue}`);
      console.log(`📍 Object: ${objectName}, Record ID: ${id}`);

      const updateData = { [fieldName]: newValue };
      const token = localStorage.getItem('token');
      const url = `${constants.API_BASE_URL}/api/object/${objectName}/${id}`;

      console.log(`📤 Calling PUT ${url}`);
      console.log(`📦 Payload:`, updateData);

      // Using axios directly for more control and clarity
      const response = await axios.put(url, updateData, {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      });

      console.log(`📥 Response:`, response);

      if (response?.data?.success || response?.status === 200) {
        console.log(`✅ Successfully updated ${fieldName} to ${newValue}`);

        // Show success notification
        setNotification({
          show: true,
          message: 'Stage updated successfully!',
          type: 'success',
        });

        // Refresh the record data to show updated stage
        await refreshRecordData();
      } else {
        throw new Error(response?.data?.message || 'Failed to update stage');
      }
    } catch (error) {
      console.error('❌ Error updating path stage:', error);
      console.error('❌ Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url
      });

      // Better error message extraction
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        'Failed to update stage';

      setNotification({
        show: true,
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setUpdatingPathField(false);
    }
  };

  // Modal handlers
  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleEditModalSave = savedData => {
    console.log('Record saved from edit modal:', savedData);
    setShowEditModal(false);

    // Show success notification
    setNotification({
      show: true,
      message: 'Record updated successfully!',
      type: 'success',
    });

    // Refresh the record data to show updated information
    refreshRecordData();
  };

  const handleEditModalCancel = () => {
    setShowEditModal(false);
  };

  // Clone Modal handlers
  const handleCloneModalClose = () => {
    setShowCloneModal(false);
  };

  const handleCloneModalSave = savedData => {
    console.log('Record cloned successfully:', savedData);
    setShowCloneModal(false);

    // Show success notification
    setNotification({
      show: true,
      message: 'Record cloned successfully!',
      type: 'success',
    });

    // Navigate to the new cloned record
    const newRecordId = savedData?.id || savedData?.data?.id;
    if (newRecordId) {
      window.location.href = `/view/${objectName}/${newRecordId}`;
    }
  };

  const handleCloneModalCancel = () => {
    setShowCloneModal(false);
  };

  const handleNewRecordModalClose = () => {
    setShowNewRecordModal(false);
  };

  const handleNewRecordModalSave = savedData => {
    setShowNewRecordModal(false);
    setNotification({
      show: true,
      message: 'Record created successfully!',
      type: 'success',
    });
    const newRecordId = savedData?.id || savedData?.data?.id;
    if (newRecordId) {
      window.location.href = `/view/${objectName}/${newRecordId}`;
    }
  };

  const handleNewRecordModalCancel = () => {
    setShowNewRecordModal(false);
  };

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const dataApi = new DataApi(`object/view/${objectName}`);
        const response = await dataApi.fetchById(id);
        const data = response?.data?.data;

        if (!data || Object.keys(data).length === 0) {
          setError('No Record Found!');
        } else {
          setRecordData(data);
          console.log('Record data loaded:', data);
          console.log('Related tabs data:', data?.tabs);
          console.log('Widgets data:', data?.widgets);
        }
      } catch (error) {
        console.error('Failed to fetch record:', error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'An unexpected error occurred.';
        setError(message);
      } finally {
        setIsLoaded(true);
      }
    };

    fetchRecord();
  }, [objectName, id]);

  useEffect(() => {
    loadedRelatedTabCacheRef.current.clear();
    inFlightRelatedTabsRef.current.clear();
    setLazyRelatedTabRecords({});
    setLazyRelatedTabLoading({});
  }, [objectName, id]);

  useEffect(() => {
    setLayoutWidgetsMountReady(false);
  }, [objectName, id]);

  useEffect(() => {
    if (!isLoaded || !recordData) return;

    const routeId = id != null && id !== '' ? String(id) : '';
    const dataRecordId =
      recordData?.recordId != null
        ? String(recordData.recordId)
        : recordData?.id != null
          ? String(recordData.id)
          : '';

    if (routeId && dataRecordId && dataRecordId !== routeId) {
      return;
    }

    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setLayoutWidgetsMountReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isLoaded, objectName, id, recordData]);

  useEffect(() => {
    if (!objectName) {
      setObjectConfig(null);
      return;
    }

    let isMounted = true;

    const fetchObjectConfig = async () => {
      try {
        const configApi = new DataApi(`objectconfig/${objectName}`);
        const response = await configApi.fetchAll();
        if (!isMounted) return;

        if (response?.data?.success) {
          setObjectConfig(response.data.data);
        } else {
          setObjectConfig(null);
        }
      } catch (error) {
        console.error('Error fetching object config:', error);
        if (isMounted) {
          setObjectConfig(null);
        }
      }
    };

    fetchObjectConfig();

    return () => {
      isMounted = false;
    };
  }, [objectName]);


  // Listen for incoming WhatsApp messages and auto-open chat
  useEffect(() => {
    if (!socket || !recordData) return;

    const phoneNumber = recordData?.details
      ?.flatMap(s => s.fields)
      ?.find(f => f.fieldName === 'phone')?.value;

    if (!phoneNumber) return;

    const handleIncomingMessage = (data) => {
      // Extract phone number from socket data
      const socketNumber = (
        data?.recipient_id ||
        data?.to ||
        data?.from ||
        ''
      ).replace(/\D/g, '');

      // Extract phone number from current record
      const currentNumber = phoneNumber?.toString().replace(/\D/g, '');

      // Open chat if the incoming message is for this record's phone number
      if (socketNumber === currentNumber) {
        setShowWhatsAppChat(true);
      }
    };

    socket.on('receivedwhatsappmessage', handleIncomingMessage);

    return () => {
      socket.off('receivedwhatsappmessage', handleIncomingMessage);
    };
  }, [socket, recordData]);

  // WhatsApp chat should only open when button is clicked, not automatically

  // Check for success message in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const successMessage = urlParams.get('success');

    if (successMessage) {
      console.log('Success message found in URL:', successMessage);
      setNotification({
        show: true,
        message: decodeURIComponent(successMessage),
        type: 'success',
      });

      // Remove success parameter from URL without page reload
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.delete('success');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, []);

  // Calculate dynamic max visible tabs based on container width
  useEffect(() => {
    const calculateMaxVisibleTabs = () => {
      if (!tabsContainerRef.current || !recordData) return;

      const container = tabsContainerRef.current;
      const containerWidth = container.offsetWidth;

      if (containerWidth === 0) return; // Container not yet rendered

      // Get all tab buttons that are currently visible
      const tabButtons = container.querySelectorAll('.tab-button:not(.tabs-dropdown-wrapper .tab-button)');

      if (tabButtons.length === 0) {
        // If no tabs rendered yet, use a default based on container width
        // Estimate: ~120px per tab, reserve 100px for "More" button
        const estimatedTabWidth = 120;
        const moreButtonWidth = 100;
        const availableWidth = containerWidth - moreButtonWidth;
        const calculatedMaxTabs = Math.max(3, Math.floor(availableWidth / estimatedTabWidth));
        setMaxVisibleTabs(Math.min(calculatedMaxTabs, 15));
        return;
      }

      // Measure actual tab widths
      let totalTabWidth = 0;
      const tabWidths = [];
      tabButtons.forEach((button) => {
        const width = button.offsetWidth;
        tabWidths.push(width);
        totalTabWidth += width;
      });

      // Calculate average tab width
      const avgTabWidth = tabWidths.length > 0 ? totalTabWidth / tabWidths.length : 120;

      // Reserve space for "More" button (if needed)
      const moreButtonWidth = 100;
      const availableWidth = containerWidth - moreButtonWidth;

      // Calculate how many tabs can fit
      const calculatedMaxTabs = Math.floor(availableWidth / avgTabWidth);

      // Ensure minimum of 3 tabs are always visible
      const minTabs = 3;
      const maxTabs = Math.max(minTabs, calculatedMaxTabs);

      // Set a reasonable maximum to prevent too many tabs
      const finalMaxTabs = Math.min(maxTabs, 20);

      setMaxVisibleTabs(finalMaxTabs);

      console.log('Dynamic tab calculation:', {
        containerWidth,
        availableWidth,
        avgTabWidth,
        calculatedMaxTabs,
        finalMaxTabs
      });
    };

    // Calculate after a short delay to ensure DOM is rendered
    const timeoutId = setTimeout(() => {
      calculateMaxVisibleTabs();
    }, 150);

    // Recalculate on window resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        calculateMaxVisibleTabs();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    // Use ResizeObserver for more accurate measurements
    let resizeObserver;
    if (tabsContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          calculateMaxVisibleTabs();
        }, 150);
      });
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [recordData]);

  const renderDetailItem = field => {
    // Extract value if field.value is an object (handle nested field objects)
    let rawValue = extractPrimitiveValue(field.value);

    // Also extract displayValue if it exists and is an object
    let rawDisplayValue = field.displayValue !== undefined
      ? extractPrimitiveValue(field.displayValue)
      : null;

    let displayValue = rawValue ?? '—';

    // TeleCMI: recording player field (preferred) + filename display field
    if (
      objectName === 'telecmi_log' &&
      (field.fieldName === 'recording' || field.fieldName === 'file_name')
    ) {
      const filename =
        typeof rawValue === 'string' && rawValue.trim() !== ''
          ? rawValue.trim()
          : getRecordFieldValue('file_name');

      const isAudio =
        typeof filename === 'string' && /\.(mp3|wav|m4a)$/i.test(filename);

      if (field.fieldName === 'recording') {
        displayValue = isAudio ? (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            role="group"
            aria-label="Recording actions"
          >
            <button
              type="button"
              title="Play recording"
              aria-label="Play recording"
              onClick={() => setTelecmiPlayer({ show: true, filename })}
              style={{
                border: 'none',
                background: '#e9ecef',
                padding: 8,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#212529',
              }}
            >
              <FaPlay size={14} />
            </button>
            <button
              type="button"
              title="Download recording"
              aria-label="Download recording"
              onClick={() => downloadTelecmiRecordingFile(filename)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 6,
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.textSecondary || '#495057',
              }}
            >
              <FaDownload size={14} />
            </button>
          </div>
        ) : (
          '—'
        );
      } else if (field.fieldName === 'file_name') {
        // file_name is now display-only (no player)
        displayValue = filename || '—';
      }
    }

    // Special handling for password fields - always show masked
    if (field.type === 'password' && rawValue) {
      displayValue = (
        <span
          style={{
            fontFamily: 'monospace',
            letterSpacing: '2px',
          }}
        >
          ••••••••
        </span>
      );
    }
    // Special handling for related_object field - use objectLabel if available
    else if (field.fieldName === 'related_object' && field.objectLabel) {
      displayValue = field.objectLabel;
    } else if (field.type === 'url') {
      displayValue = (
        <a
          href={rawValue}
          rel='noopener noreferrer'
          className='link-brand'
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.medium,
            textDecoration: 'none',
            transition: theme.transitions.normal,
            fontSize: theme.typography.fontSize.lg, // Increased from base to lg (1.125rem)
          }}
        >
          {rawValue}
        </a>
      );
    } else if (field.type === 'lookup') {
      // For lookup fields, use rawValue for the ID and displayValue for the text
      const lookupId = rawValue;
      const lookupDisplay = rawDisplayValue || rawValue || '—';
      displayValue = (
        <a
          href={`/view/${field.referenceObject}/${lookupId}`}
          rel='noopener noreferrer'
          className='link-brand'
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.medium,
            textDecoration: 'none',
            transition: theme.transitions.normal,
            fontSize: theme.typography.fontSize.lg, // Increased from base to lg (1.125rem)
          }}
        >
          {lookupDisplay}
        </a>
      );
    } else if (field.type === 'calculated') {
      displayValue = renderCalculatedField(field);
    } else if (field.type === 'date' && rawValue) {
      displayValue = formatDate(rawValue);
    } else if (field.type === 'datetime' && rawValue) {
      displayValue = formatDateTime(rawValue);
    } else if (field.type === 'time' && rawValue) {
      displayValue = formatTime(rawValue);
    } else if (field.type === 'checkbox' || field.type === 'boolean') {
      displayValue = renderReadOnlyCheckbox(rawValue);
    } else if (field.type === 'picklist' && rawValue) {
      // For picklist fields, show the displayValue (label) instead of the key
      displayValue = rawDisplayValue || rawValue;
    } else if (field.type === 'multipicklist' && rawValue) {
      // Use displayValue if available (from backend mapping), otherwise fallback to value formatting
      displayValue = rawDisplayValue
        ? String(rawDisplayValue).split(';').join(', ')
        : String(rawValue).split(';').join(', ');
    } else if (field.type === 'rollupsummary' && rawValue !== null) {
      // For rollup summary, check display_config for output type
      const displayConfig = field.displayConfig ?? field.display_config;
      const parsedConfig = parseDisplayConfig(displayConfig);
      const rollupConfig = ensurePlainObject(parsedConfig.rollupSummary);
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        if (outputType === 'currency') {
          // Format as currency
          const decimalPlaces =
            field.scale !== undefined &&
              field.scale !== null &&
              field.scale !== ''
              ? parseInt(field.scale, 10)
              : 2;
          const opts = {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          };
          const recordCurrency = getRecordFieldValue('currency');
          const currencyCode = recordCurrency || resolveCurrency('INR');
          displayValue = formatCurrency(numValue, currencyCode, opts);
          // Show base currency equivalent when record currency differs from base
          const recordExchangeRate = getRecordFieldValue('exchange_rate');
          if (
            recordCurrency &&
            displayBaseCurrencyCode &&
            String(recordCurrency).trim().toUpperCase() !== String(displayBaseCurrencyCode).trim().toUpperCase()
          ) {
            const rowRate = Number(recordExchangeRate);
            const effectiveRate = resolveEffectiveExchangeRate(
              recordCurrency,
              rowRate,
              displayExchangeRateLookup
            );
            if (Number.isFinite(effectiveRate) && effectiveRate > 0) {
              const baseEquivalent = numValue / effectiveRate;
              displayValue += ` (${formatCurrency(baseEquivalent, displayBaseCurrencyCode, opts)} ${displayBaseCurrencyCode})`;
            }
          }
        } else if (outputType === 'date') {
          // Format as date
          displayValue = formatDate(rawValue);
        } else if (outputType === 'datetime') {
          // Format as datetime
          displayValue = formatDateTime(rawValue);
        } else if (outputType === 'time') {
          // Format as time
          displayValue = formatTime(rawValue);
        } else {
          // Default to number formatting
          const options = {};
          if (
            field.scale !== undefined &&
            field.scale !== null &&
            field.scale !== ''
          ) {
            const decimalPlaces = parseInt(field.scale, 10);
            options.minimumFractionDigits = decimalPlaces;
            options.maximumFractionDigits = decimalPlaces;
          }
          displayValue = formatNumber(numValue, options);
        }
      }
    } else if (
      String(field.fieldName || '').toLowerCase() === 'exchange_rate' &&
      rawValue !== null &&
      rawValue !== undefined &&
      String(rawValue).trim() !== ''
    ) {
      if (!multiCurrencyEnabled) {
        displayValue = '—';
      } else {
        // Exchange rate must not use the currency-money branch (mis-typed layout as
        // data_type currency would show a misleading $/symbol amount). Show scaled
        // number + same "base context" pattern as currency fields.
        const numValue = Number(rawValue);
        if (!isNaN(numValue)) {
          const decimalPlaces =
            field.scale !== undefined &&
              field.scale !== null &&
              field.scale !== ''
              ? parseInt(field.scale, 10)
              : 4;
          const opts = {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          };
          displayValue = formatNumber(numValue, opts);
          const recordCurrency = getRecordFieldValue('currency');
          if (
            recordCurrency &&
            baseCurrencyCode &&
            String(recordCurrency).trim().toUpperCase() !==
            String(baseCurrencyCode).trim().toUpperCase()
          ) {
            const rec = String(recordCurrency).trim();
            const base = String(baseCurrencyCode).trim();
            displayValue += ` (1 ${base} = ${formatNumber(numValue, opts)} ${rec})`;
          } else if (baseCurrencyCode) {
            displayValue += ` (${String(baseCurrencyCode).trim()})`;
          }
        }
      }
    } else if (
      (field.type === 'number' ||
        field.type === 'decimal') &&
      rawValue !== null
    ) {
      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided
        const options = {};
        if (
          field.scale !== undefined &&
          field.scale !== null &&
          field.scale !== ''
        ) {
          const decimalPlaces = parseInt(field.scale, 10);
          options.minimumFractionDigits = decimalPlaces;
          options.maximumFractionDigits = decimalPlaces;
        }
        displayValue = formatNumber(numValue, options);
      }
    } else if (field.type === 'currency' && rawValue !== null) {
      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided, otherwise default to 2
        const decimalPlaces =
          field.scale !== undefined &&
            field.scale !== null &&
            field.scale !== ''
            ? parseInt(field.scale, 10)
            : 2;
        const opts = {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        };
        // Resolve currency: record currency > token currency (user or company) > default
        const recordCurrency = getRecordFieldValue('currency');
        const currencyCode = recordCurrency || resolveCurrency('INR');
        displayValue = formatCurrency(numValue, currencyCode, opts);
        // If record currency differs from base currency, show base equivalent e.g. "92000 ($1000 USD)"
        const recordExchangeRate = getRecordFieldValue('exchange_rate');
        if (
          recordCurrency &&
          displayBaseCurrencyCode &&
          String(recordCurrency).trim().toUpperCase() !== String(displayBaseCurrencyCode).trim().toUpperCase()
        ) {
          const rowRate = Number(recordExchangeRate);
          const effectiveRate = resolveEffectiveExchangeRate(
            recordCurrency,
            rowRate,
            displayExchangeRateLookup
          );
          if (Number.isFinite(effectiveRate) && effectiveRate > 0) {
            const baseEquivalent = numValue / effectiveRate;
            displayValue += ` (${formatCurrency(baseEquivalent, displayBaseCurrencyCode, opts)} ${displayBaseCurrencyCode})`;
          }
        }
      }
    } else if (field.type === 'richtext') {
      displayValue = (
        <div
          className='richtext-value rich-text-content'
          dangerouslySetInnerHTML={{ __html: rawValue }}
        />
      );
    } else if (field.type === 'phone' && rawValue) {
      displayValue = enableCti ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span>{String(rawValue)}</span>
          <button
            type='button'
            className='btn btn-link p-0'
            title='Call this number'
            onClick={() => handleTelecmiCallForNumber(rawValue)}
            style={{ textDecoration: 'none' }}
          >
            <i className='fas fa-phone' />
          </button>
        </span>
      ) : (
        String(rawValue)
      );
    } else if (field.type === 'barcode' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const format = typeof config.format === 'string' && config.format.trim() !== ''
        ? config.format.trim().toUpperCase()
        : 'CODE128';
      const width = toFiniteNumber(config.width) ?? 2;
      const height = toFiniteNumber(config.height) ?? 100;
      const displayBarcodeValue = config.displayValue === false || config.displayValue === 'false' || config.displayValue === 0 || config.displayValue === '0'
        ? false
        : true;

      displayValue = (
        <Barcode
          value={String(rawValue)}
          format={format}
          width={width ?? 2}
          height={height ?? 100}
          displayValue={displayBarcodeValue}
        />
      );
    } else if (field.type === 'qrcode' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const size = toFiniteNumber(config.size) ?? 200;
      const levelRaw = typeof config.errorCorrectionLevel === 'string'
        ? config.errorCorrectionLevel.toUpperCase()
        : '';
      const validLevels = ['L', 'M', 'Q', 'H'];
      const level = validLevels.includes(levelRaw) ? levelRaw : 'M';

      displayValue = (
        <QRCode
          value={String(rawValue)}
          size={size ?? 200}
          level={level}
          style={{ maxWidth: "100%", height: "auto" }}
        />
      );
    } else if (field.type === 'image' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const isBrowser = typeof window !== 'undefined';
      const isMobile = isBrowser ? window.innerWidth <= 768 : false;
      const resolveDimension = (primaryKey, secondaryKey, fallback) => {
        const primary = toFiniteNumber(config[primaryKey]);
        if (primary !== null) {
          return primary;
        }
        if (secondaryKey) {
          const secondary = toFiniteNumber(config[secondaryKey]);
          if (secondary !== null) {
            return secondary;
          }
        }
        return fallback;
      };

      const width = isMobile
        ? resolveDimension('mobileWidth', 'width', 150)
        : resolveDimension('width', null, 300);
      const height = isMobile
        ? resolveDimension('mobileHeight', 'height', 150)
        : resolveDimension('height', null, 300);
      const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
      const fitRaw = typeof config.objectFit === 'string' ? config.objectFit.toLowerCase() : '';
      const objectFit = allowedFits.includes(fitRaw) ? fitRaw : 'contain';

      displayValue = (
        <img
          src={rawValue}
          alt={field.label}
          style={{
            width: width !== null ? `${width}px` : 'auto',
            height: height !== null ? `${height}px` : 'auto',
            maxWidth: '100%',
            objectFit,
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentNode.innerHTML = '<span style="color: #999;">Image not available</span>';
          }}
        />
      );
    }

    return (
      <div className='detail-item'>
        <div className='label-value-group'>
          <div
            className='label'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.textSecondary,
              lineHeight: theme.typography.lineHeight.normal,
              fontSize: theme.typography.fontSize.lg, // Increased from base to lg (1.125rem)
            }}
          >
            {field.label}
          </div>
          <div
            className='value'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontWeight: theme.typography.fontWeight.normal,
              color: theme.colors.textPrimary,
              lineHeight: theme.typography.lineHeight.normal,
              fontSize: theme.typography.fontSize.lg, // Increased from base to lg (1.125rem)
            }}
          >
            {displayValue}
          </div>
        </div>
        {/* Hide edit icon for history objects */}
        {!recordData?.is_history_object && (
          <i
            className='fas fa-pen edit-icon'
            onClick={() => setShowEditModal(true)}
            style={{
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              transition: theme.transitions.normal,
            }}
            onMouseEnter={e => {
              e.target.style.color = theme.colors.primary;
            }}
            onMouseLeave={e => {
              e.target.style.color = theme.colors.textSecondary;
            }}
          ></i>
        )}
      </div>
    );
  };

  const renderSection = section => {
    // Get the number of columns for this section from the database
    const sectionColumns = section.layoutColumns || section.layout_columns || 2; // Default to 2 columns

    console.log(
      `Rendering section "${section.sectionName}" with ${sectionColumns} columns`
    );
    console.log('Section fields:', section.fields);

    // Get task type from record data for field visibility filtering
    const taskType =
      objectName === 'task'
        ? recordData?.details
          ?.flatMap(s => s.fields)
          ?.find(
            f =>
              f.fieldName === 'task_type' ||
              f.fieldName === 'tasktype' ||
              f.fieldName === 'type'
          )?.value
        : null;

    // Get mail config type for field visibility (SMTP, Outlook, Google)
    const mailConfigType =
      objectName === 'mail_config'
        ? recordData?.details
          ?.flatMap(s => s.fields)
          ?.find(
            f =>
              (f.fieldName && (f.fieldName === 'type' || f.fieldName === 'Type'))
          )?.value
        : null;

    // Filter fields based on task type visibility for task object
    const filteredFields = (section.fields || []).filter(field => {
      // Hide path fields from regular sections (they'll be shown as path component)
      if (field.is_path_field === true) {
        return false;
      }

      // For task object, apply field visibility rules based on task type
      if (objectName === 'task') {
        return isFieldVisible(field.fieldName, taskType);
      }
      // For mail_config object, show/hide fields based on type (SMTP, Outlook, Google)
      if (objectName === 'mail_config') {
        return isMailConfigFieldVisible(field.fieldName, mailConfigType);
      }
      return true;
    });

    // If no visible fields after filtering, don't render the section
    if (filteredFields.length === 0) {
      console.log(
        `Hiding empty section: ${section.sectionLabel || section.sectionName}`
      );
      return null;
    }

    // Create rows based on field column spans and section layout
    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;

    filteredFields.forEach(field => {
      // Calculate field width based on column span
      let fieldWidth;
      if (field.columnSpan === 2) {
        // Full width field (spans 2 columns in the section's layout)
        fieldWidth = Math.ceil(12 / sectionColumns) * 2;
      } else {
        // Regular field (spans 1 column)
        fieldWidth = Math.ceil(12 / sectionColumns);
      }

      console.log(
        `Field "${field.label}" (${field.fieldName}) - columnSpan: ${field.columnSpan}, calculated width: ${fieldWidth}`
      );

      // Check if this field would exceed the row width
      if (currentRowWidth + fieldWidth > 12) {
        // Start a new row
        if (currentRow.length > 0) {
          rows.push([...currentRow]);
          console.log('Starting new row, current row width:', currentRowWidth);
        }
        currentRow = [];
        currentRowWidth = 0;
      }

      // Add field to current row
      currentRow.push(field);
      currentRowWidth += fieldWidth;
    });

    // Add any remaining fields
    if (currentRow.length > 0) {
      rows.push([...currentRow]);
    }

    console.log('Final rows structure:', rows);

    return (
      <div className='mb-3' key={section.sectionName}>
        <h4
          className='mb-2 m-2'
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.colors.textPrimary,
            lineHeight: theme.typography.lineHeight.tight,
            fontSize: theme.typography.fontSize['2xl'], // Increased from default to 2xl (1.5rem)
          }}
        >
          {section.sectionLabel || section.sectionName}
        </h4>
        <div
          className='detail-grid'
          style={{
            gridTemplateColumns: `repeat(${sectionColumns}, 1fr)`,
            gap: '12px 20px',
            padding: '0px 16px 12px 16px',
          }}
        >
          {rows.map((fieldsInRow, rowIdx) => (
            <React.Fragment key={rowIdx}>
              {fieldsInRow.map((field, fIdx) => (
                <div
                  key={
                    field.fieldName
                      ? `${field.fieldName}-${rowIdx}-${fIdx}`
                      : `${section.sectionName}-${rowIdx}-${fIdx}`
                  }
                  style={{
                    gridColumn:
                      field.columnSpan === 2
                        ? `span ${Math.min(2, sectionColumns)}`
                        : 'span 1',
                  }}
                >
                  {renderDetailItem(field)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  if (isLoaded && error) {
    return (
      <div className='d-flex justify-content-center align-items-center w-100'>
        <div
          className='alert alert-danger text-center shadow'
          style={{ width: '70%', maxWidth: '400px' }}
        >
          <h5 className='mb-2'>Oops!</h5>
          <p className='mb-0'>{error || 'No Record Found!'}</p>
        </div>
      </div>
    );
  }

  // ✅ Optionally render a spinner while loading
  if (!isLoaded) {
    return <div className='text-center'>Loading...</div>;
  }
  return (
    <div className='account-page px-3 py-1'>
      {/* Header */}
      <div className='sf-header'>
        <div className='sf-top'>
          <div className='sf-title'>
            <i className='fas fa-building'></i>
            <div>
              <div style={{ fontSize: '13px' }}>
                <a
                  href={`/listview/${objectName}`}
                  style={{
                    color: '#5c5c5c',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = theme.colors.primary;
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#5c5c5c';
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  {detailPageObjectLabel}
                </a>
              </div>
              <h2
                style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.textSecondary,
                  fontSize: theme.typography.fontSize['3xl'], // Increased from default to 3xl (1.875rem)
                }}
              >
                {getNameFromDetails() || 'Detail'}
              </h2>
            </div>
          </div>
          <div className='sf-actions'>
            {objectName === 'email_campaigns' &&
              (() => {
                return (

                  <button
                    className='detail-action-button'
                    onClick={() => setShowAddAudience(true)}
                  >
                    <i className='fas fa-add'></i>
                    Add Audience
                  </button>
                );
              })()}

            {/* WhatsApp Button - Only show if enable_watconnect is true and phone number is available */}
            {enableWatConnect &&
              recordData?.details
                ?.flatMap(s => s.fields)
                ?.find(f => f.fieldName === 'phone')?.value && (
                <button
                  className='detail-action-button'
                  onClick={() => setShowWhatsAppChat(true)}
                  title='Send WhatsApp Message'
                >
                  <i className='fab fa-whatsapp'></i>
                  WhatsApp
                </button>
              )}

            {/* Dynamic Action Buttons from Layout Config - Only show if actions are configured */}
            {recordData?.header?.actions && recordData?.header?.actions.length > 0 && (() => {
              // Helper function to render an action button
              const renderActionButton = (action, idx, isDropdownItem = false) => {
                // Skip edit and new actions for email_log
                if (objectName?.toLowerCase() === 'email_log' && (action.action === 'edit' || action.action === 'new')) {
                  return null;
                }
                // Do not show Email action for email_template (templates are config, not a record to send from)
                if (objectName?.toLowerCase() === 'email_template' && action.action === 'email') {
                  return null;
                }

                // Handle edit action
                if (action.action === 'edit') {
                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Edit'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={() => {
                            window.location.href = `/edit/${objectName}/${id}`;
                          }}
                          title={action.label || 'Edit this record'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={() => {
                        window.location.href = `/edit/${objectName}/${id}`;
                      }}
                      title={action.label || 'Edit this record'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle clone action
                if (action.action === 'clone') {
                  // Hide clone for history objects
                  if (recordData?.is_history_object) return null;

                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Clone'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={() => setShowCloneModal(true)}
                          title={action.label || 'Clone this record'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={() => setShowCloneModal(true)}
                      title={action.label || 'Clone this record'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle new action
                if (action.action === 'new') {
                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'New'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={() => setShowNewRecordModal(true)}
                          title={action.label || 'Create new record'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={() => setShowNewRecordModal(true)}
                      title={action.label || 'Create new record'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle delete action (not allowed for user object)
                if (action.action === 'delete') {
                  if (objectName?.toLowerCase() === 'user') return null;
                  const buttonContent = (
                    <>
                      {isDeleting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          {action.icon && <i className={action.icon}></i>}
                          {action.label || 'Delete'}
                        </>
                      )}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button delete-button w-100 text-start"
                          onClick={handleDeleteRecord}
                          disabled={isDeleting}
                          title={action.label || 'Delete this record'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button delete-button"
                      onClick={handleDeleteRecord}
                      disabled={isDeleting}
                      title={action.label || 'Delete this record'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle generate_document action
                if (action.action === 'generate_document') {
                  const templateAction = action.templateAction || 'customer quote';
                  const createAttachment = action.createAttachment || false;
                  const isGenerating = generatingDocument === idx;

                  const buttonContent = isGenerating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Generate Document'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={async () => {
                            try {
                              setGeneratingDocument(idx);
                              setToast({
                                show: true,
                                message: `Generating ${action.label || 'Document'}...`,
                                variant: 'primary',
                              });
                              await GenerateDocumentApi.generateDocument(objectName, id, templateAction, createAttachment);
                              setToast({
                                show: true,
                                message: `${action.label || 'Document'} generated and downloaded successfully!${createAttachment ? ' Document has been attached to this record.' : ''}`,
                                variant: 'success',
                              });
                            } catch (error) {
                              console.error('Error generating document:', error);
                              setToast({
                                show: true,
                                message: error.message || 'Failed to generate document. Please ensure a document template is configured.',
                                variant: 'danger',
                              });
                            } finally {
                              setGeneratingDocument(null);
                            }
                          }}
                          title={action.label || 'Generate Document'}
                          disabled={isGenerating}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={async () => {
                        try {
                          setGeneratingDocument(idx);
                          setToast({
                            show: true,
                            message: `Generating ${action.label || 'Document'}...`,
                            variant: 'primary',
                          });
                          await GenerateDocumentApi.generateDocument(objectName, id, templateAction, createAttachment);
                          setToast({
                            show: true,
                            message: `${action.label || 'Document'} generated and downloaded successfully!${createAttachment ? ' Document has been attached to this record.' : ''}`,
                            variant: 'success',
                          });
                        } catch (error) {
                          console.error('Error generating document:', error);
                          setToast({
                            show: true,
                            message: error.message || 'Failed to generate document. Please ensure a document template is configured.',
                            variant: 'danger',
                          });
                        } finally {
                          setGeneratingDocument(null);
                        }
                      }}
                      title={action.label || 'Generate Document'}
                      disabled={isGenerating}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle add_products action
                if (action.action === 'add_products') {
                  // Only show for Opportunity, Quote, and Order objects
                  if (!['opportunity', 'quote', 'order'].includes(objectName?.toLowerCase())) {
                    return null;
                  }

                  // Use appropriate handler based on object type
                  const handleClick = objectName?.toLowerCase() === 'opportunity'
                    ? handleAddProducts
                    : objectName?.toLowerCase() === 'quote'
                      ? handleAddQuoteProducts
                      : handleAddOrderProducts;

                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Add Products'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={handleClick}
                          title={action.label || 'Add products to this record'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={handleClick}
                      title={action.label || 'Add products to this record'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle sync_products action
                if (action.action === 'sync_products') {
                  // Only show for Quote objects
                  if (objectName?.toLowerCase() !== 'quote') {
                    return null;
                  }

                  const opportunityId = getRecordFieldValue('opportunity');
                  // Only show if quote is linked to an opportunity
                  if (!opportunityId) {
                    return null;
                  }

                  const buttonContent = syncingProducts ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      {action.label || 'Syncing...'}
                    </>
                  ) : (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Sync Products'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={handleSyncProducts}
                          disabled={syncingProducts}
                          title={action.label || 'Sync products from quote to opportunity'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={handleSyncProducts}
                      disabled={syncingProducts}
                      title={action.label || 'Sync products from quote to opportunity'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle convert_lead action (only for lead object, only when not converted)
                if (action.action === 'convert_lead') {
                  if (objectName?.toLowerCase() !== 'lead') return null;
                  const isConverted =
                    recordData?.details?.some(section =>
                      section.fields?.some(
                        field =>
                          field.fieldName === 'is_converted' &&
                          (field.value === true || field.value === 'true')
                      )
                    ) ||
                    recordData?.otherHiddenFields?.some(
                      field =>
                        field.fieldName === 'is_converted' &&
                        (field.value === true || field.value === 'true')
                    );
                  if (isConverted) return null;

                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Convert Lead'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={() => setShowLeadConvertModal(true)}
                          title={action.label || 'Convert this lead'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={() => setShowLeadConvertModal(true)}
                      title={action.label || 'Convert this lead'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle generate_quote_from_lead: generate quote document when on a lead (creates quote from lead, then generates PDF)
                if (action.action === 'generate_quote_from_lead') {
                  if (objectName?.toLowerCase() !== 'lead') return null;

                  const isGenerating = generatingDocument === idx;
                  const buttonContent = isGenerating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Generate Quote'}
                    </>
                  );

                  const handleGenerateQuoteFromLead = async () => {
                    if (!id) {
                      setToast({ show: true, message: 'Lead ID is missing.', variant: 'danger' });
                      return;
                    }
                    try {
                      setGeneratingDocument(idx);
                      setToast({ show: true, message: 'Generating quote from lead...', variant: 'info' });
                      await GenerateDocumentApi.generateQuoteFromLead(id, action.templateAction || 'customer quote');
                      setToast({ show: true, message: 'Quote generated and downloaded successfully!', variant: 'success' });
                    } catch (error) {
                      console.error('Error generating quote from lead:', error);
                      setToast({
                        show: true,
                        message: error.message || 'Failed to generate quote. Ensure a quote document template is configured.',
                        variant: 'danger',
                      });
                    } finally {
                      setGeneratingDocument(null);
                    }
                  };

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={handleGenerateQuoteFromLead}
                          title={action.label || 'Generate quote from this lead'}
                          disabled={isGenerating}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={handleGenerateQuoteFromLead}
                      title={action.label || 'Generate quote from this lead'}
                      disabled={isGenerating}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                if (action.action === 'custom_action') {
                  const resolvedUrl = interpolateDetailCustomActionUrl(
                    action.target_url || '',
                    objectName,
                    id
                  );
                  const isComponentAction =
                    String(action.action_behavior || '').toLowerCase() === 'component' &&
                    action.component_name;
                  const isCustomScriptAction =
                    String(action.action_behavior || '').toLowerCase() === 'custom_script' &&
                    action.action_id;
                  const isExecutingScript = executingCustomScriptId === action.action_id;
                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {isCustomScriptAction && isExecutingScript
                        ? 'Processing...'
                        : action.label || action.action_name || 'Action'}
                    </>
                  );

                  const runCustomDetailAction = async () => {
                    if (isComponentAction) {
                      const { Component, blockedReason } = resolveRegisteredTabComponent(
                        action.component_name,
                        uiTabComponentAllowlist
                      );
                      if (!Component) {
                        setToast({
                          show: true,
                          message:
                            blockedReason === 'not_activated'
                              ? `Component "${action.component_name}" is not enabled in component_registry for this organization.`
                              : `Component "${action.component_name}" is not available in this application build.`,
                          variant: 'warning',
                        });
                        return;
                      }
                      const mapped = buildDetailCustomActionProps(action, {
                        layoutObjectName: objectName,
                        getFieldValueForMapping: getRelationshipFilterValue,
                      });
                      setCustomDetailActionComponent({
                        show: true,
                        title: action.label || action.action_name || 'Action',
                        Component,
                        componentProps: {
                          recordData,
                          objectName,
                          recordId: id,
                          onRefresh: refreshRecordData,
                          ...mapped,
                        },
                      });
                      return;
                    }

                    if (isCustomScriptAction) {
                      setExecutingCustomScriptId(action.action_id);
                      try {
                        const url = `${constants.API_BASE_URL}/api/custom-scripts/execute`;
                        const res = await axios.post(
                          url,
                          {
                            actionId: action.action_id,
                            recordId: id,
                            objectName,
                          },
                          {
                            headers: {
                              ...crmAuthHeaders(),
                              'Content-Type': 'application/json',
                            },
                          }
                        );
                        const data = res.data;
                        if (data.success) {
                          const payload = data.data;
                          const toastVariant = normalizeCustomScriptToastVariant(payload);
                          const redirectUrl = resolveCustomScriptRedirectUrl(payload);
                          const toastMessage = data.message || 'Script completed.';
                          if (redirectUrl) {
                            stashPendingRedirectToast({
                              message: toastMessage,
                              variant: toastVariant,
                            });
                          } else {
                            setToast({
                              show: true,
                              message: toastMessage,
                              variant: toastVariant,
                            });
                          }
                          const shouldRefresh =
                            !redirectUrl &&
                            payload &&
                            typeof payload === 'object' &&
                            payload.refreshRecord === true;
                          if (shouldRefresh) {
                            await refreshRecordData();
                          }
                          if (redirectUrl) {
                            window.location.assign(redirectUrl);
                          }
                        } else {
                          setToast({
                            show: true,
                            message: data.message || 'Script failed.',
                            variant: 'danger',
                          });
                        }
                      } catch (error) {
                        const msg =
                          error.response?.data?.message ||
                          error.message ||
                          'Script execution failed.';
                        setToast({
                          show: true,
                          message: msg,
                          variant: 'danger',
                        });
                      } finally {
                        setExecutingCustomScriptId(null);
                      }
                      return;
                    }

                    if (String(action.action_behavior || '').toLowerCase() === 'custom_script' && !action.action_id) {
                      setToast({
                        show: true,
                        message:
                          'This custom script action is missing its action id. Reload the page or re-save the layout.',
                        variant: 'warning',
                      });
                      return;
                    }

                    if (!resolvedUrl || !String(resolvedUrl).trim()) {
                      setToast({
                        show: true,
                        message: 'This action has no target URL configured.',
                        variant: 'warning',
                      });
                      return;
                    }
                    if (action.open_in_modal) {
                      setCustomDetailActionFrame({
                        show: true,
                        url: resolvedUrl,
                        title: action.label || action.action_name || 'Action',
                      });
                    } else if (/^https?:\/\//i.test(resolvedUrl)) {
                      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
                    } else {
                      window.location.href = resolvedUrl;
                    }
                  };

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          type="button"
                          className="detail-action-button w-100 text-start"
                          onClick={runCustomDetailAction}
                          title={action.label || action.action_name || ''}
                          disabled={Boolean(isCustomScriptAction && isExecutingScript)}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={idx}
                      type="button"
                      className="detail-action-button"
                      onClick={runCustomDetailAction}
                      title={action.label || action.action_name || ''}
                      disabled={Boolean(isCustomScriptAction && isExecutingScript)}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle email action
                if (action.action === 'call') {
                  if (!enableCti) {
                    return null;
                  }
                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Call'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <button
                          className="detail-action-button w-100 text-start"
                          onClick={handleTelecmiCall}
                          title={action.label || 'Initiate call'}
                        >
                          {buttonContent}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      className="detail-action-button"
                      onClick={handleTelecmiCall}
                      title={action.label || 'Initiate call'}
                    >
                      {buttonContent}
                    </button>
                  );
                }

                // Handle email action
                if (action.action === 'email') {
                  const buttonContent = (
                    <>
                      {action.icon && <i className={action.icon}></i>}
                      {action.label || 'Send Email'}
                    </>
                  );

                  if (isDropdownItem) {
                    return (
                      <div key={idx} className="p-1">
                        <EmailButton
                          recordData={recordData}
                          objectName={objectName}
                          currentUser={getCurrentUser()}
                          variant="outline-primary"
                          size="sm"
                          className="w-100"
                          showTooltip={false}
                          onEmailSent={emailData => {
                            console.log('Email sent successfully:', emailData);
                            setNotification({
                              show: true,
                              message: `Email sent successfully!`,
                              type: 'success',
                            });
                          }}
                        />
                      </div>
                    );
                  }

                  return (
                    <EmailButton
                      key={idx}
                      recordData={recordData}
                      objectName={objectName}
                      currentUser={getCurrentUser()}
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onEmailSent={emailData => {
                        console.log('Email sent successfully:', emailData);
                        setNotification({
                          show: true,
                          message: `Email sent successfully!`,
                          type: 'success',
                        });
                      }}
                    />
                  );
                }

                return null;
              };

              // Split actions into first 3 and the rest
              const actions = recordData.header.actions;
              const firstThreeActions = actions.slice(0, 3);
              const remainingActions = actions.slice(3);

              return (
                <>
                  {/* First 3 actions as buttons */}
                  {firstThreeActions.map((action, idx) => renderActionButton(action, idx, false))}

                  {/* Remaining actions in dropdown */}
                  {remainingActions.length > 0 && (
                    <Dropdown className="d-inline-block">
                      <Dropdown.Toggle className="detail-action-button" id="actions-dropdown" style={{ marginRight: 0 }}>
                        <i className="fas fa-ellipsis-v"></i>
                        More
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ minWidth: '200px', padding: '4px' }}>
                        {remainingActions.map((action, idx) => renderActionButton(action, idx + 3, true))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Highlight Section - Only render if there are highlight fields */}
        {(() => {
          // Get task type for field visibility filtering
          const taskType =
            objectName === 'task'
              ? recordData?.details
                ?.flatMap(s => s.fields)
                ?.find(
                  f =>
                    f.fieldName === 'task_type' ||
                    f.fieldName === 'tasktype' ||
                    f.fieldName === 'type'
                )?.value
              : null;

          // Get mail config type for highlight section (SMTP, Outlook, Google)
          const highlightMailConfigType =
            objectName === 'mail_config'
              ? (recordData?.highlightSection || [])
                .find(
                  f =>
                    f?.fieldName === 'type' || f?.fieldName === 'Type'
                )
                ?.value ??
              recordData?.details
                ?.flatMap(s => s.fields)
                ?.find(
                  f =>
                    f.fieldName === 'type' || f.fieldName === 'Type'
                )?.value
              : null;

          // Filter highlight fields based on task type visibility
          const filteredHighlightFields = (
            recordData?.highlightSection || []
          ).filter(field => {
            // Hide path fields from highlight section (they'll be shown as path component)
            if (field.is_path_field === true) {
              return false;
            }

            // Filter out "related_record_id" field for task object in highlight section
            if (
              objectName === 'task' &&
              field?.fieldName?.toLowerCase() === 'related_record_id'
            ) {
              return false; // Don't render this field on the UI
            }

            // Apply task type visibility rules
            if (objectName === 'task') {
              return isFieldVisible(field.fieldName, taskType);
            }

            // For mail_config, filter by type (SMTP, Outlook, Google)
            if (objectName === 'mail_config') {
              return isMailConfigFieldVisible(field.fieldName, highlightMailConfigType);
            }

            return true;
          });

          // Only render if there are visible highlight fields
          if (!filteredHighlightFields || filteredHighlightFields.length === 0) {
            return null;
          }

          return (
            <div
              className='highlight-fields'
              style={{
                gridTemplateColumns: recordData?.highlightSection?.layoutColumns
                  ? `repeat(${recordData.highlightSection.layoutColumns}, 1fr)`
                  : 'repeat(6, 1fr)',
              }}
            >
              {filteredHighlightFields.map((field, idx) => (
                <React.Fragment
                  key={field.fieldName || `highlight-${idx}`}
                >
                  {renderDetailItem(field)}
                </React.Fragment>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Path Components - Render before details */}
      {(() => {
        // Collect all path fields from all sections and highlight section
        const allFields = [
          ...(recordData?.highlightSection || []),
          ...(recordData?.details || []).flatMap(section => section.fields || [])
        ];

        const pathFields = allFields.filter(field =>
          field.is_path_field === true &&
          field.picklistValues &&
          field.picklistValues.length > 0
        );

        if (pathFields.length > 0) {
          return (
            <div className="path-fields-container">
              {pathFields.map(pathField => (
                <PathComponent
                  key={pathField.fieldName}
                  field={{
                    id: pathField.fieldName,
                    field_name: pathField.fieldName,
                    field_label: pathField.label
                  }}
                  currentValue={pathField.value}
                  onStageChange={(newValue) => handlePathStageChange(pathField.fieldName, newValue)}
                  picklistValues={pathField.picklistValues || []}
                  isUpdating={updatingPathField}
                />
              ))}
            </div>
          );
        }
        return null;
      })()}

      {/* Body */}
      <div style={{ overflowX: 'hidden', boxSizing: 'border-box', marginTop: '12px' }}>
        {(() => {
          // Get layout configuration from recordData
          const totalColumns = recordData?.total_columns || 3;
          const detailColumnPosition = recordData?.detail_column_position || 1;
          const widgets = (recordData?.widgets || []).filter((w) => {
            const t = String(w.widgetType || '').toLowerCase();
            if (t !== 'search') return true;
            const cfg = w.config || {};
            return cfg.show_in_detail_tabs === false;
          });
          const hasWidgets = widgets.length > 0;

          // Calculate column width based on total columns and column type
          const getColumnWidth = (isDetailColumn) => {
            if (totalColumns === 1) {
              // 1 column: full width
              return 12;
            } else if (totalColumns === 2) {
              // 2 columns: detail gets 70% (8.4 ≈ 8), widget gets 30% (3.6 ≈ 4)
              // Using Bootstrap grid: 8 for detail (66.67%), 4 for widget (33.33%)
              // For exact 70-30, we'll use custom width
              return isDetailColumn ? 8 : 4;
            } else {
              // 3 columns: each column gets 4 units (4-4-4 = 12) - equal split
              return 4;
            }
          };

          // Get column width for detail and widget columns
          const getColumnWidthValue = (isDetailColumn) => {
            if (totalColumns === 2) {
              // For 2 columns, use percentage-based widths for exact 70-30 split
              // Return object with custom styles and no lg prop
              return {
                customWidth: true,
                style: isDetailColumn
                  ? { flex: '0 0 70%', maxWidth: '70%', width: '70%' }
                  : { flex: '0 0 30%', maxWidth: '30%', width: '30%' }
              };
            }
            // For 1 or 3 columns, use Bootstrap grid
            return {
              customWidth: false,
              lg: getColumnWidth(isDetailColumn)
            };
          };

          // Determine which columns widgets should appear in
          const getWidgetColumns = () => {
            if (totalColumns === 1) {
              return [];
            }

            const widgetColumns = [];
            for (let i = 1; i <= totalColumns; i++) {
              if (i !== detailColumnPosition) {
                widgetColumns.push(i);
              }
            }
            return widgetColumns;
          };

          const widgetColumns = getWidgetColumns();

          // Filter widgets by their column position
          const getWidgetsForColumn = (columnNum) => {
            return widgets.filter(w => w.widgetColumnPosition === columnNum);
          };

          return (
            <Row style={{ width: '100%', margin: 0, marginLeft: 0, marginRight: 0 }}>
              {/* Render columns based on configuration */}
              {Array.from({ length: totalColumns }, (_, index) => {
                const columnNum = index + 1;
                const isDetailColumn = columnNum === detailColumnPosition;
                const columnWidgets = isDetailColumn ? [] : getWidgetsForColumn(columnNum);

                if (isDetailColumn) {
                  // Detail Layout Column
                  const detailWidth = getColumnWidthValue(true);
                  return (
                    <Col
                      key={`detail-${columnNum}`}
                      xs={12}
                      {...(detailWidth.customWidth ? {} : { lg: detailWidth.lg })}
                      style={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...(detailWidth.customWidth ? detailWidth.style : {})
                      }}
                    >
                      <div className='left-panel'>
                        {/* Tabs */}
                        {(() => {
                          const allTabs = [
                            { tabName: 'detailsTab', label: 'Details' },
                            ...normalizedCustomTabs.map(tab => ({ tabName: tab.tabName, label: tab.label })),
                            ...detailTabsMerged.map(tab => ({
                              tabName: tab.tabName,
                              label: tab.displayLabel || tab.tabName
                            }))
                          ];

                          // Use dynamic maxVisibleTabs calculated from container width
                          const visibleTabs = allTabs.slice(0, maxVisibleTabs);
                          const overflowTabs = allTabs.slice(maxVisibleTabs);



                          return (
                            <div className='tabs' ref={tabsContainerRef}>
                              {/* Visible tabs */}
                              {visibleTabs.map(tab => (
                                <button
                                  key={tab.tabName}
                                  className={`tab-button ${activeTab === tab.tabName ? 'active' : ''}`}
                                  onClick={() => switchTab(tab.tabName)}
                                >
                                  {tab.label}
                                </button>
                              ))}

                              {/* Dropdown for overflow tabs - show when there are overflow tabs */}
                              {overflowTabs.length > 0 && (
                                <div
                                  ref={tabsDropdownRef}
                                  className="tabs-dropdown-wrapper"
                                  style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    zIndex: 1000,
                                    overflow: 'visible'
                                  }}
                                >
                                  <button
                                    type="button"
                                    className={`tab-button ${overflowTabs.some(t => activeTab === t.tabName) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('More button clicked, current state:', showTabsDropdown);
                                      const newState = !showTabsDropdown;
                                      console.log('Setting state to:', newState);
                                      setShowTabsDropdown(newState);
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      background: overflowTabs.some(t => activeTab === t.tabName) ? '#f4f6f9' : 'none',
                                      border: 'none',
                                      fontSize: '14px',
                                      cursor: 'pointer',
                                      fontWeight: 500,
                                      color: overflowTabs.some(t => activeTab === t.tabName) ? '#0176d3' : '#555',
                                      borderBottom: overflowTabs.some(t => activeTab === t.tabName) ? '3px solid #0176d3' : '3px solid transparent',
                                      boxShadow: 'none'
                                    }}
                                  >
                                    More <i className={`fas fa-chevron-${showTabsDropdown ? 'up' : 'down'} ms-1`}></i>
                                  </button>
                                  {/* Portal for dropdown menu to avoid z-index and overflow issues */}
                                  {showTabsDropdown && typeof document !== 'undefined' && (() => {
                                    // Calculate position for fixed positioning
                                    const buttonRect = tabsDropdownRef.current?.querySelector('button')?.getBoundingClientRect();

                                    if (!buttonRect) return null;

                                    const menuContent = (
                                      <div
                                        id="tabs-dropdown-portal"
                                        className="tabs-dropdown-menu"
                                        style={{
                                          position: 'fixed',
                                          top: `${buttonRect.bottom + 2}px`,
                                          left: `${buttonRect.left}px`,
                                          display: 'block',
                                          visibility: 'visible',
                                          opacity: 1,
                                          maxHeight: '400px',
                                          overflowY: 'auto',
                                          zIndex: 10050,
                                          minWidth: '200px',
                                          backgroundColor: '#fff',
                                          border: '1px solid #dee2e6',
                                          borderRadius: '0.375rem',
                                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                          marginTop: '0',
                                          padding: '4px 0',
                                          transform: 'none',
                                          willChange: 'auto'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        {overflowTabs.map(tab => (
                                          <button
                                            key={tab.tabName}
                                            type="button"
                                            className={`dropdown-item ${activeTab === tab.tabName ? 'active' : ''}`}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              switchTab(tab.tabName);
                                              setShowTabsDropdown(false);
                                            }}
                                            style={{
                                              display: 'block',
                                              width: '100%',
                                              padding: '10px 20px',
                                              textAlign: 'left',
                                              color: activeTab === tab.tabName ? '#0176d3' : '#212529',
                                              fontWeight: activeTab === tab.tabName ? 600 : 400,
                                              backgroundColor: activeTab === tab.tabName ? '#f4f6f9' : 'transparent',
                                              border: 'none',
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                              if (activeTab !== tab.tabName) {
                                                e.target.style.backgroundColor = '#f8f9fa';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (activeTab !== tab.tabName) {
                                                e.target.style.backgroundColor = 'transparent';
                                              }
                                            }}
                                          >
                                            {tab.label}
                                          </button>
                                        ))}
                                      </div>
                                    );

                                    return createPortal(menuContent, document.body);
                                  })()}
                                </div>
                              )}

                            </div>
                          );
                        })()}

                        {/* Detail Sections */}
                        <div
                          className={`tab-content ${activeTab === 'detailsTab' ? 'active' : ''
                            }`}
                        >
                          {recordData?.details?.map(section => renderSection(section))}
                        </div>

                        {/* Related List Tabs */}
                        {normalizedCustomTabs.length > 0 && normalizedCustomTabs.map(tab => {
                          if (tab?.type !== 'component') {
                            return null;
                          }

                          const { Component: TabComponent, blockedReason } = resolveRegisteredTabComponent(
                            tab?.component,
                            uiTabComponentAllowlist
                          );
                          if (!TabComponent) {
                            const msg =
                              blockedReason === 'not_activated'
                                ? `Component "${tab?.component}" is not enabled in component_registry for this organization.`
                                : `Component "${tab?.component}" is not registered in the app.`;
                            return (
                              <div
                                key={tab.tabName}
                                className={`tab-content ${activeTab === tab.tabName ? 'active' : ''}`}
                              >
                                <div className="no-related-tabs">
                                  <p>{msg}</p>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={tab.tabName}
                              className={`tab-content ${activeTab === tab.tabName ? 'active' : ''}`}
                            >
                              <TabComponent
                                recordData={recordData}
                                objectName={objectName}
                                recordId={id}
                                onRefresh={refreshRecordData}
                              />
                            </div>
                          );
                        })}

                        {detailTabsMerged.length > 0 ? (
                          <RelatedTabsModal
                            tabs={detailTabsMerged}
                            activeTab={activeTab}
                            onTabChange={switchTab}
                            onRefresh={refreshRecordData}
                            onRelatedListRowsRefresh={refreshActiveRelatedTabRowsOnly}
                            currentRecordId={id}
                            sourceObjectName={objectName}
                            recordData={recordData}
                            onBulkEditRequest={handleBulkEditRequest}
                            relatedTabLoadingById={lazyRelatedTabLoading}
                            onRelatedListTabRefresh={handleRelatedListTabRefresh}
                            getRelationshipFilterValue={getRelationshipFilterValue}
                          />
                        ) : (
                          recordData && (
                            <div className='no-related-tabs'>
                              <p>No related lists or search views configured for this record.</p>
                            </div>
                          )
                        )}
                      </div>
                    </Col>
                  );
                } else {
                  // Widget Column - Always render if it's a widget column, even if empty
                  // Always render widget columns to maintain layout structure
                  const widgetWidth = getColumnWidthValue(false);
                  return (
                    <Col
                      key={`widget-${columnNum}`}
                      xs={12}
                      {...(widgetWidth.customWidth ? {} : { lg: widgetWidth.lg })}
                      style={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...(widgetWidth.customWidth ? widgetWidth.style : {})
                      }}
                    >
                      <div className='widgets-panel' style={{ width: '100%' }}>
                        {/* Widgets for this column */}
                        {columnWidgets.length > 0 && (
                          <div className="widget-column">
                            {layoutWidgetsMountReady ? (
                              columnWidgets.map(widget => (
                                <LayoutWidget
                                  key={widget.widgetId}
                                  widget={{ ...widget, recordData }}
                                  onRefresh={refreshRecordData}
                                  currentRecordId={id}
                                  sourceObjectName={objectName}
                                  onShowAll={handleWidgetShowAll}
                                />
                              ))
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center py-4 text-muted small"
                                aria-busy="true"
                              >
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Loading widgets…
                              </div>
                            )}
                          </div>
                        )}

                        {/* Empty state - show nothing if no widgets */}
                        {columnWidgets.length === 0 && (
                          <div style={{ minHeight: '50px' }}></div>
                        )}
                      </div>
                    </Col>
                  );
                }
              })}
            </Row>
          );
        })()}
      </div>

      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        duration={5000}
        onClose={() =>
          setNotification({ show: false, message: '', type: 'success' })
        }
      />

      {/* Lead Convert Modal */}
      {objectName === 'lead' && (
        <LeadConvertModal
          show={showLeadConvertModal}
          onHide={() => setShowLeadConvertModal(false)}
          leadData={recordData}
          onConvert={result => {
            if (result.success) {
              setNotification({
                show: true,
                message: result.message,
                type: 'success',
              });

              // Redirect based on configured preference
              const redirectPreference = result?.redirectPreference || 'opportunity';
              let targetObject = null;
              let targetId = null;

              // Determine redirect target based on preference
              if (result?.customObjects?.[redirectPreference]) {
                targetObject = redirectPreference;
                targetId = result.customObjects[redirectPreference];
              } else if (redirectPreference === 'opportunity' && result?.opportunity?.id) {
                targetObject = 'opportunity';
                targetId = result.opportunity.id;
              } else if (redirectPreference === 'account' && result?.account?.id) {
                targetObject = 'account';
                targetId = result.account.id;
              } else if (redirectPreference === 'contact' && result?.contact?.id) {
                targetObject = 'contact';
                targetId = result.contact.id;
              } else {
                // Fallback: try opportunity, then account, then contact
                if (result?.customObjects && Object.keys(result.customObjects).length > 0) {
                  const [firstCustomObject, firstCustomId] = Object.entries(result.customObjects)[0];
                  targetObject = firstCustomObject;
                  targetId = firstCustomId;
                } else if (result?.opportunity?.id) {
                  targetObject = 'opportunity';
                  targetId = result.opportunity.id;
                } else if (result?.account?.id) {
                  targetObject = 'account';
                  targetId = result.account.id;
                } else if (result?.contact?.id) {
                  targetObject = 'contact';
                  targetId = result.contact.id;
                }
              }

              if (targetObject && targetId) {
                // Use setTimeout to allow notification to show briefly before redirect
                setTimeout(() => {
                  window.location.href = `/view/${targetObject}/${targetId}`;
                }, 1500); // 1.5 second delay to show the success message
              }
            }
          }}
        />
      )}

      <Modal
        show={customDetailActionFrame.show}
        onHide={() => setCustomDetailActionFrame({ show: false, url: '', title: '' })}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{customDetailActionFrame.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
          {customDetailActionFrame.url ? (
            <iframe
              title={customDetailActionFrame.title}
              src={customDetailActionFrame.url}
              style={{ width: '100%', height: '70vh', border: 'none' }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            />
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={customDetailActionComponent.show}
        onHide={() =>
          setCustomDetailActionComponent({
            show: false,
            title: '',
            Component: null,
            componentProps: null,
          })
        }
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{customDetailActionComponent.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {(() => {
            const Comp = customDetailActionComponent.Component;
            const props = customDetailActionComponent.componentProps;
            return Comp && props ? <Comp {...props} /> : null;
          })()}
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <EditLayoutModal
        show={showEditModal}
        onHide={handleEditModalClose}
        objectName={objectName}
        id={id}
        onSave={handleEditModalSave}
        onCancel={handleEditModalCancel}
        sourcePage='detail'
        onRefresh={refreshRecordData}
      />

      {/* Clone Modal */}
      <EditLayoutModal
        show={showCloneModal}
        onHide={handleCloneModalClose}
        objectName={objectName}
        id={id}
        isClone={true}
        onSave={handleCloneModalSave}
        onCancel={handleCloneModalCancel}
        sourcePage='detail'
        onRefresh={refreshRecordData}
      />

      {/* New record (record view picker + form in modal) */}
      <EditLayoutModal
        show={showNewRecordModal}
        onHide={handleNewRecordModalClose}
        objectName={objectName}
        id={null}
        onSave={handleNewRecordModalSave}
        onCancel={handleNewRecordModalCancel}
        sourcePage="detail"
      />

      <BulkEditLayout
        show={bulkEditState.show}
        onHide={() =>
          setBulkEditState({
            show: false,
            tab: null,
            selectedRecords: [],
            parentRecordId: null,
            parentObjectName: null,
          })
        }
        tab={bulkEditState.tab}
        selectedRecords={bulkEditState.selectedRecords}
        parentRecordId={bulkEditState.parentRecordId}
        parentObjectName={bulkEditState.parentObjectName}
      />
      <OpportunityProductWizard
        show={showProductWizard}
        onHide={handleProductWizardClose}
        opportunityId={id}
        opportunityCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleProductWizardSaveSuccess}
        initialItems={existingLineItems}
        initialItemsLoading={loadingLineItems}
      />
      <QuoteProductWizard
        show={showQuoteProductWizard}
        onHide={handleQuoteProductWizardClose}
        quoteId={id}
        quoteCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleQuoteProductWizardSaveSuccess}
      />
      <OrderProductWizard
        show={showOrderProductWizard}
        onHide={handleOrderProductWizardClose}
        orderId={id}
        orderCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleOrderProductWizardSaveSuccess}
      />
      {/* WhatsApp Chat Component */}
      <WatConnectChat
        socket={socket}
        selectedWhatsAppSetting={selectedWhatsAppSetting}
        isOpen={showWhatsAppChat}
        onToggle={() => setShowWhatsAppChat(!showWhatsAppChat)}
        onClose={() => setShowWhatsAppChat(false)}
        recipientName={
          recordData?.details
            ?.flatMap(s => s.fields)
            ?.find(f => f.fieldName === 'first_name')?.value || '' + ' ' + recordData?.details
              ?.flatMap(s => s.fields)
              ?.find(f => f.fieldName === 'last_name')?.value || ''
        }
        recipientPhone={
          recordData?.details
            ?.flatMap(s => s.fields)
            ?.find(f => f.fieldName === 'phone')?.value || ''
        }
      />
      {/* AppToast for error messages */}
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        sticky={toast.variant === 'danger'}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />

      {/* Delete Confirmation Modal; success/error shown in same modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          const shouldRedirect = deleteSuccessRedirectRef.current;
          deleteSuccessRedirectRef.current = false;
          setShowDeleteModal(false);
          setDeleteResultMessage(null);
          setDeleteChildrenChecked(false);
          if (shouldRedirect) window.location.href = `/listview/${objectName}`;
        }}
        onConfirm={confirmDeleteRecord}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        itemName={recordData?.header?.titleField ? getRecordFieldValue(recordData.header.titleField) : `${objectName} record`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        resultMessage={deleteResultMessage?.text ?? null}
        resultVariant={deleteResultMessage?.variant ?? 'danger'}
        isDeleting={isDeleting}
        showDeleteChildrenCheckbox
        deleteChildrenChecked={deleteChildrenChecked}
        onDeleteChildrenCheckboxChange={setDeleteChildrenChecked}
      />


      {/* Audience Modal */}
      <AudienceModal
        show={showAddAudience}
        onHide={() => setShowAddAudience(false)}
        objectName={objectName}
        recordData={recordData}
        getNameFromDetails={getNameFromDetails}
      />

      {/* Related List View Modal from Widget */}
      {showRelatedListViewModal && currentWidgetTab && currentWidgetTab.config?.target_object_name && (
        <RelatedListView
          show={showRelatedListViewModal}
          onHide={() => {
            setShowRelatedListViewModal(false);
            setCurrentWidgetTab(null);
            // Clean up window object
            delete window.currentModalTab;
          }}
          objectName={currentWidgetTab.config.target_object_name}
          parentField={currentWidgetTab.config?.target_field_name}
          parentId={id}
          parentFilterValue={getRelationshipFilterValue(currentWidgetTab.config?.source_field_name)}
          title={`All ${currentWidgetTab.tabName || 'Related Records'}`}
          allowedActions={currentWidgetTab.config?.actions || ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export']}
        />
      )}

      <TelecmiRecordingPlayerModal
        show={telecmiPlayer.show}
        filename={telecmiPlayer.filename}
        onHide={() => setTelecmiPlayer({ show: false, filename: null })}
      />
    </div>
  );
};

export default DetailView;
